var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/transport/envelopes.ts
function isEventMessage(msg) {
  return "Command" in msg && msg.Command === "EventMessage";
}
function isSystemEvent(msg) {
  if ("Event" in msg) return true;
  if ("Command" in msg && SYSTEM_EVENT_NAMES.has(msg.Command)) {
    return true;
  }
  return false;
}
function isCommandResponse(msg) {
  if (!("Command" in msg)) return false;
  if (SYSTEM_EVENT_NAMES.has(msg.Command)) return false;
  return true;
}
function normalizeSystemEvent(msg) {
  if ("Event" in msg && typeof msg.Event === "string") {
    return msg;
  }
  if ("Command" in msg && typeof msg.Command === "string") {
    const cmd = msg.Command;
    if (SYSTEM_EVENT_NAMES.has(cmd)) {
      const payload = msg.Payload;
      return {
        Event: cmd,
        Payload: payload !== null && typeof payload === "object" ? payload : {}
      };
    }
  }
  return null;
}
function isErrorResponse(msg) {
  return "Error" in msg.Payload && msg.Payload.Error !== void 0;
}
function normalizeInboundCommand(msg) {
  if ("Command" in msg && typeof msg.Command === "string") {
    const cmd = msg.Command;
    const dot = cmd.lastIndexOf(".");
    if (dot >= 0) {
      return { ...msg, Command: cmd.slice(dot + 1) };
    }
  }
  return msg;
}
function readRequestId(payload) {
  const id = payload["RequestId"] ?? payload["RequestID"];
  return typeof id === "number" ? id : void 0;
}
function parseInnerPayload(payload) {
  if (!payload) return {};
  try {
    const parsed = JSON.parse(payload);
    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
    return {};
  } catch {
    return {};
  }
}
var SYSTEM_EVENT_NAMES = /* @__PURE__ */ new Set([
  "KeepAlive",
  "SessionStateEvent",
  "DataSubscriptionUpdate",
  "RPCMaintenanceEvent",
  "SchemaChangedEvent"
]);
var Commands = {
  // Proxy / handshake (PSV-1271 M1).
  HandshakeRequest: "HandshakeRequest",
  HandshakeResponse: "HandshakeResponse",
  ClientSettingsRequest: "ClientSettingsRequest",
  ClientSettingsResponse: "ClientSettingsResponse",
  // TODO(PSV-1271): rework login after auth design decision — current backend
  // has no Login phase, GameAccessToken in HandshakeRequest is the sole auth
  // mechanism. These names are retained so the existing login() shim and
  // its tests keep compiling until the auth model is finalised.
  LoginRequest: "LoginRequest",
  LoginResponse: "LoginResponse",
  // PSV-1271 M-Wire-Fix: backend command name is `InvokeRpc`, not the
  // SRS-SA-TP draft `RPCMethodInvoke`. Source: `playserv-backend-main/src/
  // PlayServ.Module.Rpc/Commands/InvokeRpcCommandHandler.cs:16` and
  // `Shared.Runtime/RPC/RpcConstants.cs:11`.
  // TODO(PSV-1271 backend B-stretch): confirm InvokeRpc is final wire-name
  // (SRS-SA-TP used RPCMethodInvoke). Asked backend team.
  InvokeRpc: "InvokeRpc",
  InvokeRpcResponse: "InvokeRpcResponse",
  DataGetRequest: "DataGetRequest",
  DataGetResponse: "DataGetResponse",
  DataMutationRequest: "DataMutationRequest",
  DataMutationResponse: "DataMutationResponse",
  DataSubscriptionRequest: "DataSubscriptionRequest",
  DataSubscriptionResponse: "DataSubscriptionResponse",
  // `EventMessage` is the inbound event-delivery name (also the KeepAlive pong
  // shape). Group membership is managed with the Subscribe/Unsubscribe pair;
  // clients do not publish, so there are no outbound event-message commands.
  EventMessage: "EventMessage",
  SubscribeGroupRequest: "SubscribeGroupRequest",
  SubscribeGroupResponse: "SubscribeGroupResponse",
  UnsubscribeGroupRequest: "UnsubscribeGroupRequest",
  UnsubscribeGroupResponse: "UnsubscribeGroupResponse"
};
var COMMAND_TO_MODULE = Object.freeze({
  // Auth
  [Commands.ClientSettingsRequest]: "module_auth",
  // Events module owns the group subscription commands.
  [Commands.SubscribeGroupRequest]: "module_events",
  [Commands.UnsubscribeGroupRequest]: "module_events",
  // RPC topic is the un-prefixed `rpc` (Module.Rpc/appsettings.json).
  [Commands.InvokeRpc]: "rpc",
  // DataFlow
  [Commands.DataGetRequest]: "module_dataflow",
  [Commands.DataMutationRequest]: "module_dataflow",
  [Commands.DataSubscriptionRequest]: "module_dataflow"
});
function prefixBusCommand(name) {
  const mod = COMMAND_TO_MODULE[name];
  return mod === void 0 ? name : `${mod}.${name}`;
}

// src/core/errors.ts
var PlayServError = class extends Error {
  constructor(error) {
    super(error.Message);
    this.name = "PlayServError";
    this.code = error.Code;
    this.details = error.Details;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var AuthError = class extends PlayServError {
  constructor(error) {
    super(error);
    this.name = "AuthError";
  }
};
var GameVersionMismatchError = class extends AuthError {
  constructor(message, details) {
    super({ Code: ErrorCodes.GAME_VERSION_MISMATCH, Message: message, Details: details });
    this.name = "GameVersionMismatchError";
    this.serverVersion = typeof details.server_version === "string" ? details.server_version : void 0;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var RpcExecutionError = class extends PlayServError {
  constructor(error) {
    super(error);
    this.name = "RpcExecutionError";
    const details = error.Details ?? {};
    this.originalCode = typeof details.original_code === "number" ? details.original_code : void 0;
    this.originalMessage = typeof details.original_message === "string" ? details.original_message : void 0;
    this.data = typeof details.data === "object" && details.data !== null ? details.data : void 0;
  }
};
var DataConflictError = class extends PlayServError {
  constructor(error) {
    super(error);
    this.name = "DataConflictError";
    const details = error.Details ?? {};
    this.serverVersion = typeof details.server_version === "number" ? details.server_version : void 0;
    this.clientVersion = typeof details.client_version === "number" ? details.client_version : void 0;
  }
};
var NotFoundError = class extends PlayServError {
  constructor(error) {
    super(error);
    this.name = "NotFoundError";
    const details = error.Details ?? {};
    this.entity = typeof details.entity === "string" ? details.entity : void 0;
    this.targetId = typeof details.target_id === "string" ? details.target_id : typeof details.id === "string" ? details.id : void 0;
  }
};
var ValidationError = class extends PlayServError {
  constructor(error) {
    super(error);
    this.name = "ValidationError";
    const details = error.Details ?? {};
    this.field = typeof details.field === "string" ? details.field : void 0;
  }
};
var EventSubscriptionLimitExceededError = class extends PlayServError {
  constructor(message, details = {}) {
    super({
      Code: ErrorCodes.MAX_EVENT_SUBSCRIPTIONS,
      Message: message,
      Details: details
    });
    this.name = "EventSubscriptionLimitExceededError";
  }
};
var GroupLogicError = class extends PlayServError {
  constructor(error) {
    super(error);
    this.name = "GroupLogicError";
    const details = error.Details ?? {};
    this.reasonCode = typeof details.reason_code === "number" ? details.reason_code : void 0;
    this.reasonMessage = typeof details.reason_message === "string" ? details.reason_message : void 0;
  }
};
var ConnectionError = class extends Error {
  constructor(message, opts = {}) {
    super(message);
    this.name = "ConnectionError";
    this.code = opts.code;
    this.reason = opts.reason;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var ProtocolError = class extends Error {
  constructor(message, payload) {
    super(message);
    this.name = "ProtocolError";
    this.payload = payload;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var ContractMismatchError = class extends Error {
  constructor(service, method, reason, raw) {
    super(`RPC contract mismatch: ${service}.${method} \u2014 ${reason}`);
    this.name = "ContractMismatchError";
    this.service = service;
    this.method = method;
    this.raw = raw;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var DuplicateKeyError = class extends Error {
  constructor(entity, id) {
    super(`Duplicate key: ${entity}#${id} already exists`);
    this.name = "DuplicateKeyError";
    this.entity = entity;
    this.id = id;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var TimeoutError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "TimeoutError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var NotReadyError = class extends Error {
  constructor() {
    super("SDK not ready");
    this.name = "NotReadyError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var NotSupportedError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "NotSupportedError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var IllegalStateError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "IllegalStateError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var SessionBannedError = class extends Error {
  constructor(message = "Session is banned \u2014 application restart required") {
    super(message);
    this.name = "SessionBannedError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var InvalidArgumentError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "InvalidArgumentError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var ErrorCodes = {
  // Module.Proxy / Transport (handshake & session) — confirmed
  INVALID_HANDSHAKE_PAYLOAD: 1,
  SDK_VERSION_UNSUPPORTED: 1002,
  GAME_VERSION_MISMATCH: 1003,
  CONNECTION_LIMIT_REACHED: 2001,
  SESSION_FORCE_REJECTED: 2002,
  FORCED_DISCONNECT: 2003,
  INTERNAL_SERVER_ERROR: 5e4,
  // TODO(PSV-1271): legacy SRS-SA-TP transport codes below — kept under their
  // SRS numeric form until backend confirms whether equivalents exist.
  AUTH_TOKEN_INVALID: 1001,
  SESSION_CREATION_FAILED: 9001,
  // Module.Routing — TODO(PSV-1271): backend confirmation pending
  INVALID_JSON: 10001,
  NO_COMMAND_NAME: 10002,
  NO_PAYLOAD: 10003,
  NO_REQUEST_IDENTIFIER: 10004,
  UNKNOWN_COMMAND: 11001,
  COMMAND_VERSION_MISMATCH: 11002,
  SERVER_EXCEPTION: 19001,
  // Module.RPC — backend (`Module.Rpc/Commands/InvokeRpcCommandHandler.cs`)
  // currently emits failures as `{message, status: "error"}` with no numeric
  // codes; see `docs/BACKEND_DIAG.md` B4. The SRS-SA-TP placeholders 20001-29xxx
  // were aspirational and have been removed. `RpcExecutionError` survives as
  // a class so M-Smoke can route real codes once backend introduces them.
  // TODO(PSV-1271 backend B-stretch): repopulate when backend numeric codes land.
  // Module.Data — TODO(PSV-1271): backend confirmation pending
  INVALID_QUERY_SYNTAX: 30001,
  UNKNOWN_FIELD: 30002,
  INVALID_VARIABLES: 30003,
  PATCH_FORMAT_INVALID: 30004,
  SCHEMA_VIOLATION: 30005,
  DATA_ACCESS_DENIED: 31001,
  TARGET_NOT_FOUND: 31002,
  SUBSCRIPTION_ID_NOT_FOUND: 31003,
  MAX_SUBSCRIPTIONS_REACHED: 32001,
  QUERY_RESULT_TOO_LARGE: 32002,
  VERSION_CONFLICT: 32003,
  DELETE_PROHIBITED: 32004,
  SUBSCRIPTION_TERMINATED: 32005,
  SUBSCRIPTION_FAILED: 39001,
  STREAM_INTERRUPTED: 39002,
  PERSISTENCE_FAILED: 39003,
  // Confirmed from `playserv-backend` `PlayServ.Core.Types/Enums/ErrorCode.cs`:
  // `MutationTargetNotFound = 92002`. NOTE: the DataFlow mutation/get handlers
  // do NOT emit it — client mutations upsert-create on a missing key, and a
  // keyed `get()` of a missing object resolves to `null`. It is emitted today
  // only by `Module.Versioning` (project version bump/state/maintenance).
  // `TARGET_NOT_FOUND: 31002` above is the SRS-SA-TP placeholder; the real
  // backend not-found code is this one. Both route to `NotFoundError`.
  MUTATION_TARGET_NOT_FOUND: 92002,
  // Module.Events — confirmed values from playserv-backend
  INVALID_TOPIC_FORMAT: 70001,
  UNKNOWN_TOPIC: 70002,
  INVALID_SUBSCRIPTION_ID_FORMAT: 70003,
  SUBSCRIPTION_NOT_FOUND: 70004,
  EVENT_ACCESS_DENIED: 71001,
  UNSUBSCRIBE_FORBIDDEN: 71002,
  MAX_EVENT_SUBSCRIPTIONS: 79001,
  EVENT_SUBSCRIPTION_FAILED: 79002,
  EVENT_UNSUBSCRIBE_FAILED: 79003,
  // Module.Groups — TODO(PSV-1271): backend confirmation pending
  GROUP_NOT_FOUND: 50001,
  INVALID_GROUP_NAME: 50002,
  USER_NOT_IN_GROUP: 50003,
  INVALID_JOIN_CREDENTIALS: 51001,
  USER_BANNED_IN_GROUP: 51002,
  NOT_AUTHORIZED_TO_CLOSE: 51003,
  GROUP_IS_FULL: 52001,
  GROUP_STATE_INVALID: 52002,
  GROUP_LOGIC_REJECTION: 52003,
  LEADER_CANNOT_LEAVE: 52004,
  LEAVE_FORBIDDEN_IN_STATE: 52005,
  GROUP_CREATION_LIMIT: 52006,
  GROUP_SYSTEM_ERROR: 59001,
  GROUP_LEAVE_FAILED: 59002,
  GROUP_INFRA_FAILED: 59003
};
function errorSubsystem(code) {
  if (code === 5e4) return "system";
  if (code >= 1 && code < 1e4) return "transport";
  if (code >= 1e4 && code < 2e4) return "routing";
  if (code >= 2e4 && code < 3e4) return "rpc";
  if (code >= 3e4 && code < 4e4) return "data";
  if (code >= 50001 && code < 6e4) return "groups";
  if (code >= 7e4 && code < 8e4) return "events";
  return "unknown";
}
function errorClass(code) {
  if (code === 5e4) return "system";
  if (code < 1e4) return "unknown";
  const classDigit = Math.floor(code % 1e4 / 1e3);
  switch (classDigit) {
    case 0:
      return "validation";
    case 1:
      return "access";
    case 2:
      return "logic";
    case 9:
      return "system";
    default:
      return "unknown";
  }
}
var FATAL_SESSION_CODES = /* @__PURE__ */ new Set([
  ErrorCodes.SDK_VERSION_UNSUPPORTED,
  ErrorCodes.GAME_VERSION_MISMATCH,
  ErrorCodes.CONNECTION_LIMIT_REACHED,
  ErrorCodes.SESSION_FORCE_REJECTED,
  ErrorCodes.FORCED_DISCONNECT
]);
var HANDSHAKE_AUTH_CODES = /* @__PURE__ */ new Set([
  ErrorCodes.INVALID_HANDSHAKE_PAYLOAD,
  ErrorCodes.SDK_VERSION_UNSUPPORTED,
  ErrorCodes.GAME_VERSION_MISMATCH,
  ErrorCodes.CONNECTION_LIMIT_REACHED,
  ErrorCodes.SESSION_FORCE_REJECTED,
  ErrorCodes.FORCED_DISCONNECT
]);
function classifyHandshakeErrorCode(code, message, details = {}) {
  if (code === ErrorCodes.GAME_VERSION_MISMATCH) {
    return new GameVersionMismatchError(message, details);
  }
  if (HANDSHAKE_AUTH_CODES.has(code)) {
    return new AuthError({ Code: code, Message: message, Details: details });
  }
  return new PlayServError({ Code: code, Message: message, Details: details });
}
var AUTH_CODES = /* @__PURE__ */ new Set([
  ErrorCodes.INVALID_HANDSHAKE_PAYLOAD,
  ErrorCodes.AUTH_TOKEN_INVALID,
  ErrorCodes.SDK_VERSION_UNSUPPORTED,
  ErrorCodes.GAME_VERSION_MISMATCH,
  ErrorCodes.CONNECTION_LIMIT_REACHED,
  ErrorCodes.SESSION_FORCE_REJECTED,
  ErrorCodes.FORCED_DISCONNECT,
  ErrorCodes.SESSION_CREATION_FAILED
]);
function classifyProtocolError(error) {
  const code = error.Code;
  if (code === ErrorCodes.GAME_VERSION_MISMATCH) {
    return new GameVersionMismatchError(error.Message, error.Details ?? {});
  }
  if (AUTH_CODES.has(code)) return new AuthError(error);
  if (code === ErrorCodes.VERSION_CONFLICT) return new DataConflictError(error);
  if (code === ErrorCodes.TARGET_NOT_FOUND || code === ErrorCodes.MUTATION_TARGET_NOT_FOUND) {
    return new NotFoundError(error);
  }
  if (code === ErrorCodes.SCHEMA_VIOLATION) return new ValidationError(error);
  if (code === ErrorCodes.GROUP_LOGIC_REJECTION) return new GroupLogicError(error);
  if (code === ErrorCodes.MAX_EVENT_SUBSCRIPTIONS) {
    return new EventSubscriptionLimitExceededError(error.Message, error.Details ?? {});
  }
  return new PlayServError(error);
}

// src/core/protocolValidation.ts
function typeOf(value) {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  const t = typeof value;
  return t;
}
function getPath(source, path) {
  const segments = path.split(".");
  let current = source;
  for (const seg of segments) {
    if (current === null || current === void 0 || typeof current !== "object") {
      return void 0;
    }
    current = current[seg];
  }
  return current;
}
function requireField(source, path, type, context) {
  const value = getPath(source, path);
  const actual = typeOf(value);
  if (actual !== type) {
    const where = context !== void 0 ? ` (${context})` : "";
    throw new ProtocolError(
      `Missing or wrong-typed field '${path}'${where}: expected ${type}, got ${actual}`,
      source
    );
  }
  return value;
}
function optionalField(source, path, type) {
  const value = getPath(source, path);
  if (value === void 0) return void 0;
  const actual = typeOf(value);
  if (actual !== type) return void 0;
  return value;
}

// src/transport/Heartbeat.ts
var Heartbeat = class {
  constructor(opts) {
    this.opts = opts;
    this.watchdogTimer = null;
  }
  start() {
    this.stop();
    this.armWatchdog();
  }
  /** Record a KeepAlive receipt. Call ONLY on server's KeepAlive — not on other traffic. */
  beat() {
    this.armWatchdog();
  }
  stop() {
    if (this.watchdogTimer !== null) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }
  armWatchdog() {
    if (this.watchdogTimer !== null) clearTimeout(this.watchdogTimer);
    this.watchdogTimer = setTimeout(() => this.opts.onTimeout(), this.opts.watchdogMs);
  }
};

// src/utils/backoff.ts
var DEFAULT_BACKOFF = {
  baseMs: 500,
  maxMs: 3e4,
  jitter: 0.2
};
function computeBackoff(attempt, opts = void 0, random = Math.random) {
  const { baseMs, maxMs, jitter } = { ...DEFAULT_BACKOFF, ...opts };
  const exp = Math.min(baseMs * 2 ** Math.max(0, attempt), maxMs);
  const jit = exp * (1 + (random() * 2 - 1) * jitter);
  return Math.max(0, Math.round(jit));
}

// src/utils/logger.ts
var LEVEL_ORDER = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 99
};
var Logger = class _Logger {
  constructor(opts = {}) {
    this.level = opts.level ?? "info";
    this.prefix = opts.prefix ?? "playserv";
    this.sink = opts.sink ?? defaultSink;
  }
  setLevel(level) {
    this.level = level;
  }
  child(suffix) {
    return new _Logger({ level: this.level, prefix: `${this.prefix}:${suffix}`, sink: this.sink });
  }
  debug(...args) {
    this.write("debug", args);
  }
  info(...args) {
    this.write("info", args);
  }
  warn(...args) {
    this.write("warn", args);
  }
  error(...args) {
    this.write("error", args);
  }
  write(level, args) {
    if (LEVEL_ORDER[level] >= LEVEL_ORDER[this.level]) {
      this.sink(level, this.prefix, args);
    }
  }
};
var defaultSink = (level, prefix, args) => {
  const fn = level === "error" ? console.error : level === "warn" ? console.warn : level === "debug" ? console.debug : console.log;
  fn(`[${prefix}]`, ...args);
};

// src/transport/WebSocketTransport.ts
function isKeepAlivePing(msg) {
  if (typeof msg !== "object" || msg === null) return false;
  const payload = msg.Payload;
  if (typeof payload !== "object" || payload === null) return false;
  return payload.Event === "KeepAlive";
}
var WebSocketTransport = class {
  constructor(opts) {
    this.opts = opts;
    this._state = "idle";
    this.ws = null;
    this.attempts = 0;
    this.userClosed = false;
    this.reconnectTimer = null;
    this.log = opts.logger ?? new Logger({ prefix: "playserv:ws" });
    this.reconnectOpts = opts.reconnect;
    this.listeners = {
      open: /* @__PURE__ */ new Set(),
      message: /* @__PURE__ */ new Set(),
      close: /* @__PURE__ */ new Set(),
      error: /* @__PURE__ */ new Set()
    };
    const expectedPingMs = opts.keepAliveIntervalMs ?? 3e4;
    const watchdogMs = opts.keepAliveWatchdogMs ?? expectedPingMs * 2;
    this.heartbeat = new Heartbeat({
      watchdogMs,
      onTimeout: () => this.onHeartbeatTimeout()
    });
  }
  get state() {
    return this._state;
  }
  async connect() {
    if (this.userClosed) throw new Error("Transport closed \u2014 create a new instance");
    if (this._state === "open") return;
    if (this._state === "connecting") throw new Error("Already connecting");
    await this.connectOnce();
  }
  send(message) {
    if (!this.ws || this._state !== "open") {
      throw new Error(`Transport not open (state=${this._state})`);
    }
    const serialized = JSON.stringify(message);
    try {
      this.ws.send(serialized);
    } catch (err) {
      this.log.warn("ws.send threw during mid-close transition; frame dropped", err);
    }
  }
  async close() {
    this.userClosed = true;
    this.heartbeat.stop();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const ws = this.ws;
    this.ws = null;
    const wasActive = this._state !== "closed";
    if (ws) {
      try {
        ws.close(1e3, "client close");
      } catch (err) {
        this.log.warn("ws.close threw", err);
      }
    }
    if (wasActive) {
      this._state = "closed";
      this.emit("close", { code: 1e3, reason: "client close", willReconnect: false });
    }
    return Promise.resolve();
  }
  forceReconnect() {
    if (this.userClosed) return;
    const ws = this.ws;
    if (ws === null) return;
    try {
      ws.close(4e3, "client forced reconnect");
    } catch (err) {
      this.log.warn("forceReconnect ws.close threw", err);
    }
  }
  on(event, handler) {
    this.listeners[event].add(handler);
  }
  off(event, handler) {
    this.listeners[event].delete(handler);
  }
  emit(event, ...args) {
    for (const listener of this.listeners[event]) {
      try {
        listener(...args);
      } catch (err) {
        this.log.error("listener threw", err);
      }
    }
  }
  connectOnce() {
    this._state = "connecting";
    const WS = this.opts.WebSocketImpl ?? globalThis.WebSocket;
    if (!WS) {
      this._state = "closed";
      return Promise.reject(new Error("No WebSocket implementation available"));
    }
    return new Promise((resolve, reject) => {
      let settled = false;
      const ws = new WS(this.opts.endpoint);
      this.ws = ws;
      ws.onopen = () => {
        this._state = "open";
        this.attempts = 0;
        this.heartbeat.start();
        this.log.debug("ws open");
        this.emit("open");
        settled = true;
        resolve();
      };
      ws.onmessage = (ev) => {
        this.handleRawMessage(ev.data);
      };
      ws.onerror = () => {
        this.log.warn("ws error event");
        this.emit("error", new Error("WebSocket error event"));
      };
      ws.onclose = (ev) => {
        this.handleClose(ev.code, ev.reason, settled, reject);
        settled = true;
      };
    });
  }
  handleClose(code, reason, hadOpened, reject) {
    this.log.debug("ws close", code, reason);
    this.heartbeat.stop();
    this.ws = null;
    if (this.userClosed) {
      this._state = "closed";
      if (!hadOpened) reject(new Error(`Closed before open (${code})`));
      return;
    }
    if (!hadOpened) {
      this._state = "closed";
      const info2 = { code, reason, willReconnect: false };
      this.emit("close", info2);
      reject(new Error(`Closed before open (${code})`));
      return;
    }
    this._state = "connecting";
    const info = { code, reason, willReconnect: true };
    this.emit("close", info);
    const delay = computeBackoff(this.attempts, this.reconnectOpts);
    this.attempts += 1;
    this.log.info(`reconnect in ${String(delay)}ms (attempt ${String(this.attempts)})`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.userClosed) return;
      this.connectOnce().catch((err) => {
        this.log.warn("reconnect attempt failed", err);
        if (this.userClosed) return;
        const again = computeBackoff(this.attempts, this.reconnectOpts);
        this.attempts += 1;
        this.reconnectTimer = setTimeout(() => {
          this.reconnectTimer = null;
          if (!this.userClosed) {
            this.connectOnce().catch(() => {
            });
          }
        }, again);
      });
    }, delay);
  }
  handleRawMessage(data) {
    if (typeof data !== "string") {
      this.emit("error", new Error("Non-string WebSocket frame received"));
      return;
    }
    let msg;
    try {
      msg = JSON.parse(data);
    } catch {
      this.emit("error", new Error("Invalid JSON frame"));
      return;
    }
    if (isKeepAlivePing(msg)) {
      this.handleKeepAlivePing();
      return;
    }
    this.emit("message", msg);
  }
  /**
   * PSV-1271 M3.6: server-driven KeepAlive. Backend
   * (`Module.Proxy/Transport/Handlers/KeepAliveHandler.cs:104-115`) sends
   * `{Command:"EventMessage", Payload:{Event:"KeepAlive", Payload:{}}}` and
   * expects ANY frame back whose `Payload.Event == "KeepAlive"` (line 61-75).
   * We echo the same shape so it round-trips unambiguously, reset our own
   * watchdog, and never propagate the frame to message listeners — this is
   * a transport-private side channel, callers must not see KeepAlive in
   * their event subscriptions.
   */
  handleKeepAlivePing() {
    this.heartbeat.beat();
    if (!this.ws || this._state !== "open") return;
    const pong = {
      Command: "EventMessage",
      Payload: { Event: "KeepAlive", Payload: {} }
    };
    const serialized = JSON.stringify(pong);
    try {
      this.ws.send(serialized);
    } catch (err) {
      this.log.warn("keepalive pong send failed", err);
    }
  }
  onHeartbeatTimeout() {
    this.log.warn("heartbeat watchdog fired \u2014 forcing reconnect");
    if (this.ws) {
      try {
        this.ws.close(4e3, "heartbeat-timeout");
      } catch {
      }
    }
  }
};

// src/utils/storage.ts
function memoryStorage() {
  const map = /* @__PURE__ */ new Map();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    }
  };
}
function defaultStorage() {
  const g = globalThis;
  if (g.localStorage && typeof g.localStorage.getItem === "function") {
    return g.localStorage;
  }
  return memoryStorage();
}

// src/utils/deferred.ts
var Deferred = class {
  constructor() {
    let res;
    let rej;
    this.promise = new Promise((r, j) => {
      res = r;
      rej = j;
    });
    this.resolve = res;
    this.reject = rej;
  }
};

// src/core/CommandDispatcher.ts
var CommandDispatcher = class {
  constructor(opts) {
    /** Primary index: by RequestId. Always populated. */
    this.pending = /* @__PURE__ */ new Map();
    /**
     * Secondary index: by expected response `Command` name. Populated only
     * when the caller passed `responseCommand`. Each entry is a FIFO queue
     * — older requests are matched first if multiple share the same
     * response name.
     */
    this.pendingByName = /* @__PURE__ */ new Map();
    this.errorHooks = [];
    this.nextRequestId = 1;
    this.closed = false;
    this.transport = opts.transport;
    this.defaultTimeoutMs = opts.defaultTimeoutMs;
    this.log = opts.logger;
  }
  /**
   * Send a command and expose both the generated RequestId and the response
   * Promise. Callers that need to correlate early push events to their own
   * request (e.g. DataSubscription — decision #8) use the RequestId.
   *
   * Pass `opts.responseCommand` for any subsystem where the backend does
   * NOT echo `RequestId` (handshake, auth, events, RPC) — see file
   * docstring.
   */
  requestWithId(command, payload = {}, opts = {}) {
    if (this.closed) {
      return { requestId: -1, promise: Promise.reject(new NotReadyError()) };
    }
    const id = this.nextRequestId++;
    const msg = {
      Command: prefixBusCommand(command),
      Payload: { ...payload, RequestId: id }
    };
    const deferred = new Deferred();
    const timeoutMs = opts.timeoutMs ?? this.defaultTimeoutMs;
    const pending = {
      requestId: id,
      deferred,
      timeout: null,
      command,
      responseCommand: opts.responseCommand
    };
    pending.timeout = timeoutMs > 0 ? setTimeout(() => {
      if (this.removePending(pending)) {
        deferred.reject(new TimeoutError(`Command timeout: ${command}`));
      }
    }, timeoutMs) : null;
    this.pending.set(id, pending);
    if (opts.responseCommand !== void 0) {
      const queue = this.pendingByName.get(opts.responseCommand) ?? [];
      queue.push(pending);
      this.pendingByName.set(opts.responseCommand, queue);
    }
    try {
      this.transport.send(msg);
    } catch (err) {
      this.removePending(pending);
      return {
        requestId: id,
        promise: Promise.reject(err instanceof Error ? err : new Error(String(err)))
      };
    }
    return { requestId: id, promise: deferred.promise };
  }
  /** Thin wrapper over `requestWithId` for callers that don't need the id. */
  request(command, payload = {}, opts = {}) {
    return this.requestWithId(command, payload, opts).promise;
  }
  /**
   * Hook for protocol-level observers that want to react to error-shaped
   * responses (e.g. ProtocolController transitions the session to `banned`
   * on fatal handshake errors). Hooks run synchronously before the pending
   * promise rejects.
   */
  onErrorResponse(hook) {
    this.errorHooks.push(hook);
    return () => {
      const idx = this.errorHooks.indexOf(hook);
      if (idx >= 0) this.errorHooks.splice(idx, 1);
    };
  }
  /**
   * Route an incoming command response to its pending deferred.
   *
   * Resolution order (M-Dispatcher-Hybrid):
   *   1. If `Payload.RequestId` is present and a pending exists for it →
   *      RequestId path (DataFlow).
   *   2. Else, if `msg.Command` matches the head of a name-keyed queue →
   *      name-match path (handshake/auth/events/RPC).
   *   3. Else → log at warn level and drop.
   */
  dispatchResponse(msg) {
    const id = readRequestId(msg.Payload);
    if (id !== void 0) {
      const pending = this.pending.get(id);
      if (pending !== void 0) {
        this.removePending(pending);
        this.completePending(pending, msg, id);
        return;
      }
    }
    const queue = this.pendingByName.get(msg.Command);
    if (queue !== void 0 && queue.length > 0) {
      const pending = queue.shift();
      if (queue.length === 0) this.pendingByName.delete(msg.Command);
      this.pending.delete(pending.requestId);
      if (pending.timeout !== null) clearTimeout(pending.timeout);
      this.completePending(pending, msg, pending.requestId);
      return;
    }
    if (id === void 0 && msg.Command === "ErrorResponse" && this.pendingByName.size > 0) {
      let oldest;
      let oldestQueue;
      let oldestKey;
      for (const [name, q] of this.pendingByName) {
        const head = q[0];
        if (head !== void 0 && (oldest === void 0 || head.requestId < oldest.requestId)) {
          oldest = head;
          oldestQueue = q;
          oldestKey = name;
        }
      }
      if (oldest !== void 0 && oldestQueue !== void 0 && oldestKey !== void 0) {
        oldestQueue.shift();
        if (oldestQueue.length === 0) this.pendingByName.delete(oldestKey);
        this.pending.delete(oldest.requestId);
        if (oldest.timeout !== null) clearTimeout(oldest.timeout);
        this.completePending(oldest, msg, oldest.requestId);
        return;
      }
    }
    if (id === void 0) {
      this.log.debug(
        `response with no RequestId and no pending name-match for "${msg.Command}"`
      );
    } else {
      this.log.warn(`no pending command for RequestId ${String(id)}`, msg.Command);
    }
  }
  /** Resolve / reject a pending entry from a CommandResponse. */
  completePending(pending, msg, matchedId) {
    const err = extractWireError(msg);
    if (err !== null) {
      for (const hook of this.errorHooks) {
        try {
          hook(pending.command, matchedId, err);
        } catch (hookErr) {
          this.log.error("error-response hook threw", hookErr);
        }
      }
      pending.deferred.reject(classifyProtocolError(err));
      return;
    }
    pending.deferred.resolve(msg.Payload);
  }
  /**
   * Remove `pending` from both indexes and clear its timeout. Returns
   * `true` if the entry was actually present (i.e. caller should reject
   * with a timeout); `false` if it was already gone (response raced
   * timeout, or transport.send failed mid-bookkeeping).
   */
  removePending(pending) {
    const wasInPrimary = this.pending.delete(pending.requestId);
    if (pending.responseCommand !== void 0) {
      const queue = this.pendingByName.get(pending.responseCommand);
      if (queue !== void 0) {
        const idx = queue.indexOf(pending);
        if (idx >= 0) queue.splice(idx, 1);
        if (queue.length === 0) this.pendingByName.delete(pending.responseCommand);
      }
    }
    if (pending.timeout !== null) clearTimeout(pending.timeout);
    return wasInPrimary;
  }
  /** Reject every in-flight command with the supplied error. */
  rejectAllPending(err) {
    if (this.pending.size === 0) return;
    for (const pending of this.pending.values()) {
      if (pending.timeout !== null) clearTimeout(pending.timeout);
      pending.deferred.reject(err);
    }
    this.pending.clear();
    this.pendingByName.clear();
  }
  get isClosed() {
    return this.closed;
  }
  get inFlightCount() {
    return this.pending.size;
  }
  close(err) {
    if (this.closed) return;
    this.closed = true;
    this.rejectAllPending(err);
  }
};
function toWireError(raw) {
  const o = typeof raw === "object" && raw !== null ? raw : {};
  const code = typeof o["Code"] === "number" ? o["Code"] : typeof o["ErrorCode"] === "number" ? o["ErrorCode"] : 0;
  const message = typeof o["Message"] === "string" ? o["Message"] : "protocol error";
  const details = typeof o["Details"] === "object" && o["Details"] !== null ? o["Details"] : void 0;
  return details !== void 0 ? { Code: code, Message: message, Details: details } : { Code: code, Message: message };
}
function extractWireError(msg) {
  const p = msg.Payload;
  if (p["Error"] !== void 0 && p["Error"] !== null) {
    return toWireError(p["Error"]);
  }
  const result = p["Result"];
  if (typeof result === "object" && result !== null) {
    const r = result;
    if (r["Success"] === false && r["Error"] !== void 0) {
      return toWireError(r["Error"]);
    }
  }
  if (msg.Command === "ErrorResponse") {
    return toWireError(p);
  }
  return null;
}

// src/core/EventRouter.ts
var EventRouter = class {
  constructor(log) {
    this.log = log;
    this.systemListeners = /* @__PURE__ */ new Map();
    this.eventMessageListeners = /* @__PURE__ */ new Set();
  }
  onSystemEvent(eventName, listener) {
    let set = this.systemListeners.get(eventName);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.systemListeners.set(eventName, set);
    }
    set.add(listener);
    return () => {
      set?.delete(listener);
    };
  }
  onEventMessage(listener) {
    this.eventMessageListeners.add(listener);
    return () => {
      this.eventMessageListeners.delete(listener);
    };
  }
  dispatchSystemEvent(msg) {
    const listeners = this.systemListeners.get(msg.Event);
    if (!listeners) return;
    for (const listener of listeners) {
      try {
        listener(msg.Payload);
      } catch (err) {
        this.log.error("system event listener threw", err);
      }
    }
  }
  dispatchEventMessage(msg) {
    const eventType = msg.Payload.EventType;
    const data = parseInnerPayload(msg.Payload.Payload);
    for (const listener of this.eventMessageListeners) {
      try {
        listener(eventType, data);
      } catch (err) {
        this.log.error("event-message listener threw", err);
      }
    }
  }
  /** Clear all listeners. Called on `Connection.close()` cleanup. */
  clear() {
    this.systemListeners.clear();
    this.eventMessageListeners.clear();
  }
  get debugSnapshot() {
    return {
      topicsWithListeners: [...this.systemListeners.keys()],
      eventMessageListenerCount: this.eventMessageListeners.size
    };
  }
};

// src/core/Subscription.ts
function createSubscription(cancel) {
  let stopped = false;
  return {
    stop() {
      if (stopped) return;
      stopped = true;
      cancel();
    }
  };
}

// src/core/Session.ts
var STATE_TO_KEY = {
  offline: "Offline",
  recovering: "Recovering",
  awaitingClientSettings: "AwaitingClientSettings",
  active: "Active",
  banned: "Banned"
};
var Session = class {
  constructor() {
    this._state = "offline";
    this._userId = null;
    this.listeners = {
      Update: /* @__PURE__ */ new Set(),
      Active: /* @__PURE__ */ new Set(),
      Recovering: /* @__PURE__ */ new Set(),
      AwaitingClientSettings: /* @__PURE__ */ new Set(),
      Offline: /* @__PURE__ */ new Set(),
      Banned: /* @__PURE__ */ new Set()
    };
  }
  get state() {
    return this._state;
  }
  get userId() {
    return this._userId;
  }
  /**
   * Subscribe to state changes.
   * `Update` fires on every transition; specific keys fire only when the
   * session enters that state. Subscriptions do NOT fire for the current
   * state at subscribe time — read `.state` if an initial snapshot is needed.
   */
  wait(key, cb) {
    this.listeners[key].add(cb);
    return createSubscription(() => {
      this.listeners[key].delete(cb);
    });
  }
  /** @internal */
  _transitionTo(state, details) {
    if (this._state === state) return;
    this._state = state;
    const ev = { state };
    if (details !== void 0) ev.details = details;
    this.fire("Update", ev);
    this.fire(STATE_TO_KEY[state], ev);
  }
  /** @internal */
  _setUserId(id) {
    this._userId = id;
  }
  fire(key, ev) {
    for (const listener of this.listeners[key]) {
      try {
        listener(ev);
      } catch {
      }
    }
  }
  /** Diagnostic snapshot (CODE_REVIEW.md §3.4). */
  get debugSnapshot() {
    return {
      state: this._state,
      userId: this._userId,
      listenersByKey: {
        Update: this.listeners.Update.size,
        Active: this.listeners.Active.size,
        Recovering: this.listeners.Recovering.size,
        AwaitingClientSettings: this.listeners.AwaitingClientSettings.size,
        Offline: this.listeners.Offline.size,
        Banned: this.listeners.Banned.size
      }
    };
  }
};

// src/core/Connection.ts
var CLIENT_PUBLISH_UNSUPPORTED = "Client-side event publishing is not supported by the platform (events are published server-side from an @server function / game-server, or via a remote() RPC that publishes). Clients receive events with wait()/onEventMessage and change state with remote() or model().save().";
var Connection = class {
  constructor(opts) {
    this.opts = opts;
    this.rehydrateHooks = [];
    /**
     * Player session JWT (PSV-1696 phase 2), set by `bringOnline(token)` from
     * `PlayServ.ready(sessionToken)`. When present it is relayed in the
     * `HandshakeRequest` as `Authorization: "Bearer …"` (alongside `ClientToken`
     * = the pk_ public key) so the platform resolves a `Player` runtime caller
     * and binds the session to the cheat-proof `player_id` — see
     * `playserv-platform` `HandshakeRequestCommandHandler`. Null ⇒ legacy
     * caller-less handshake (5 fields only).
     */
    this.playerToken = null;
    /**
     * Set once the deployed platform rejected our runtime key in its dedicated
     * handshake slot (errorCode=1) — all subsequent handshakes (including
     * reconnect re-handshakes) go legacy caller-less. See doHandshake.
     */
    this.runtimeSlotBlocked = false;
    this.clientSettings = null;
    this.online = false;
    this.closed = false;
    // ---------- Transport event handlers ----------
    this.handleTransportOpen = () => {
      if (!this.online) return;
      this.log.info("transport reopened \u2014 running rehydrate");
      void this.runReconnectFlow();
    };
    this.handleTransportClose = (info) => {
      if (this.closed) return;
      this.dispatcher.rejectAllPending(
        new ConnectionError(`Transport closed (${String(info.code)})`, {
          code: info.code,
          reason: info.reason
        })
      );
      if (this.session.state === "banned") return;
      if (info.willReconnect) {
        this.session._transitionTo("recovering");
      } else {
        this.online = false;
        this.session._transitionTo("offline");
      }
    };
    this.handleTransportError = (err) => {
      this.log.warn("transport error", err);
    };
    /**
     * Order matters here. `EventMessage` is `Command:`-shaped — `isCommandResponse`
     * would say true for it too. The specific `isEventMessage` guard runs first;
     * the dispatcher only sees genuine responses.
     */
    this.handleMessage = (raw) => {
      const msg = normalizeInboundCommand(raw);
      if (isEventMessage(msg)) {
        this.router.dispatchEventMessage(msg);
        return;
      }
      if (isCommandResponse(msg) && msg.Command === Commands.ClientSettingsResponse) {
        this.log.debug("ignoring late ClientSettingsResponse (fire-and-forget mode)");
        return;
      }
      if (isCommandResponse(msg)) {
        this.dispatcher.dispatchResponse(msg);
        return;
      }
      if (isSystemEvent(msg)) {
        const normalized = normalizeSystemEvent(msg);
        if (normalized !== null) {
          this.handleSystemEvent(normalized);
        }
      }
    };
    this.transport = opts.transport;
    this.session = opts.session;
    this.log = opts.logger ?? new Logger({ prefix: "playserv:conn" });
    const defaultTimeoutMs = opts.defaultTimeoutMs ?? 1e4;
    this.session._setUserId(opts.userId);
    this.dispatcher = new CommandDispatcher({
      transport: this.transport,
      defaultTimeoutMs,
      logger: this.log
    });
    this.router = new EventRouter(this.log);
    this.transport.on("open", this.handleTransportOpen);
    this.transport.on("message", this.handleMessage);
    this.transport.on("close", this.handleTransportClose);
    this.transport.on("error", this.handleTransportError);
  }
  // ---------- Public API ----------
  /**
   * Connect, perform handshake, fetch client settings, transition the session
   * to `active`. `token` is the player session JWT (PSV-1696): when set it is
   * relayed in the handshake as `Authorization: "Bearer …"`; when null the
   * handshake runs in legacy caller-less mode.
   */
  async bringOnline(token = null) {
    if (this.closed) throw new Error("Connection already closed \u2014 create a new instance");
    if (this.online) return;
    this.playerToken = token;
    this.session._transitionTo("recovering");
    try {
      await this.transport.connect();
      await this.doHandshake();
      this.session._transitionTo("awaitingClientSettings");
      this.clientSettings = this.doClientSettings();
      await Promise.resolve();
      this.online = true;
      this.session._transitionTo("active");
    } catch (err) {
      if (!this.closed) {
        try {
          await this.transport.close();
        } catch {
        }
      }
      if (this.session.state !== "banned") {
        this.session._transitionTo("offline");
      }
      throw err;
    }
  }
  /**
   * TODO(PSV-1271): rework login after auth design decision.
   *
   * Stub kept compiling against the legacy `LoginRequest`/`LoginResponse`
   * shape (`Status`, `UserInfo.UserId`, `Token`, `RefreshToken`). The real
   * backend has no Login endpoint — `GameAccessToken` in `HandshakeRequest`
   * is the sole auth mechanism. Tests against this stub mock the wire
   * format directly, so the stub stays green even though no live server
   * implements it.
   */
  async login(externalToken) {
    const resp = await this.request(Commands.LoginRequest, { UserToken: externalToken });
    const userId = resp.UserInfo?.UserId;
    if (!userId) {
      throw new ProtocolError("LoginResponse missing UserInfo.UserId", resp);
    }
    const token = resp.Token ?? externalToken;
    if (resp.Token === void 0) {
      this.log.warn(
        "LoginResponse.Payload.Token missing \u2014 falling back to externalToken (PSV-1271 stub)."
      );
    }
    const refreshToken = resp.RefreshToken;
    this.playerToken = token;
    this.session._setUserId(userId);
    return { token, userId, refreshToken };
  }
  /**
   * Replace the player JWT relayed on subsequent handshakes (PSV-1696 phase 3).
   * Called after a token refresh so the next reconnect re-handshakes with the
   * fresh, non-expired token. Does not touch the live connection — the current
   * handshake already completed.
   */
  setPlayerToken(token) {
    this.playerToken = token;
  }
  /**
   * Register a hook fired for every error-shaped command response, before the
   * pending promise rejects (PSV-1696 phase 3 — used to drive re-auth on a live
   * auth failure). Returns an unsubscribe function.
   */
  onErrorResponse(hook) {
    return this.dispatcher.onErrorResponse(hook);
  }
  request(command, payload = {}, opts = {}) {
    if (this.closed) return Promise.reject(new NotReadyError());
    return this.dispatcher.request(command, payload, opts);
  }
  requestWithId(command, payload = {}, opts = {}) {
    if (this.closed) {
      return { requestId: -1, promise: Promise.reject(new NotReadyError()) };
    }
    return this.dispatcher.requestWithId(command, payload, opts);
  }
  /**
   * Fire-and-forget send: a transport-level write with NO pending entry, no
   * response correlation, and no timeout timer. For platform hot paths that
   * by contract never answer — `rpc.InvokeRpc` with `FireAndForget: true`
   * returns nothing (`InvokeRpcHandler` one-way branch), so a
   * `dispatcher.request` pending would sit until its timer fires.
   *
   * `RequestId: -1` mirrors `doClientSettings` — the wire convention for
   * "no correlation expected". Throws synchronously when the transport is
   * not open (same contract as the dispatcher path mid-reconnect); a 20 Hz
   * input loop just skips the tick.
   */
  notify(command, payload = {}) {
    if (this.closed) throw new NotReadyError();
    const msg = {
      Command: prefixBusCommand(command),
      Payload: { ...payload, RequestId: -1 }
    };
    this.transport.send(msg);
  }
  /**
   * PSV-1271 P7: client-side event publishing is NOT supported by the platform.
   * Publishing is server-side only (game_server / cloud_function via the uplink;
   * see `playserv-platform` — a client-sent `EventMessage` hits no handler and is
   * silently dropped). Throws `NotSupportedError` rather than send-and-lose.
   * Clients receive events (`wait` / `onEventMessage`) and change state via
   * `remote()` RPC or `model().save()`.
   */
  sendEventMessage(_eventType, _payload = {}) {
    throw new NotSupportedError(CLIENT_PUBLISH_UNSUPPORTED);
  }
  /** Not supported — see {@link sendEventMessage}. Publish group events server-side. */
  sendGroupEvent(_groupName, _eventType, _payload = {}) {
    throw new NotSupportedError(CLIENT_PUBLISH_UNSUPPORTED);
  }
  /** Not supported — see {@link sendEventMessage}. There are no per-user events on the platform. */
  sendUserEvent(_userId, _eventType, _payload = {}) {
    throw new NotSupportedError(CLIENT_PUBLISH_UNSUPPORTED);
  }
  onSystemEvent(eventName, listener) {
    return this.router.onSystemEvent(eventName, listener);
  }
  /**
   * Subscribe to broadcast `EventMessage` deliveries. Listener receives
   * the EventType and the parsed inner payload (the wire `Payload` field
   * is a JSON string — `EventRouter` does the parse).
   */
  onEventMessage(listener) {
    return this.router.onEventMessage(listener);
  }
  onRehydrate(hook) {
    this.rehydrateHooks.push(hook);
    return () => {
      const idx = this.rehydrateHooks.indexOf(hook);
      if (idx >= 0) this.rehydrateHooks.splice(idx, 1);
    };
  }
  async close() {
    if (this.closed) return;
    this.closed = true;
    this.online = false;
    this.dispatcher.close(new ConnectionError("Connection closed", { code: 1e3 }));
    this.router.clear();
    try {
      await this.transport.close();
    } catch (err) {
      this.log.warn("transport.close threw", err);
    }
    if (this.session.state !== "banned") {
      this.session._transitionTo("offline");
    }
  }
  get isOnline() {
    return this.online;
  }
  /** Settings returned by the most recent `ClientSettingsResponse`, or null pre-handshake. */
  get settings() {
    return this.clientSettings;
  }
  get debugSnapshot() {
    return {
      online: this.online,
      closed: this.closed,
      transportState: this.transport.state,
      dispatcher: {
        inFlight: this.dispatcher.inFlightCount,
        closed: this.dispatcher.isClosed
      },
      router: this.router.debugSnapshot,
      rehydrateHookCount: this.rehydrateHooks.length,
      clientSettings: this.clientSettings
    };
  }
  // ---------- Protocol-level orchestration ----------
  async runReconnectFlow() {
    this.session._transitionTo("recovering");
    try {
      await this.doHandshake();
      this.session._transitionTo("awaitingClientSettings");
      this.clientSettings = this.doClientSettings();
      await Promise.resolve();
      if (this.rehydrateHooks.length > 0) {
        const results = await Promise.allSettled(
          this.rehydrateHooks.map((hook) => Promise.resolve().then(() => hook()))
        );
        for (const result of results) {
          if (result.status === "rejected") {
            this.log.warn("rehydrate hook failed", result.reason);
          }
        }
      }
      this.session._transitionTo("active");
    } catch (err) {
      if (this.session.state === "banned") {
        this.log.error("re-handshake failed with a fatal code \u2014 closing", err);
        await this.close();
        return;
      }
      this.log.warn("re-handshake failed transiently \u2014 retrying via reconnect", err);
      this.session._transitionTo("recovering");
      this.transport.forceReconnect();
    }
  }
  async doHandshake() {
    const payload = {
      GameAccessToken: this.opts.gameAccessToken,
      GameId: this.opts.gameId,
      UserId: this.opts.userId,
      GameVersion: this.opts.gameVersion,
      SdkVersion: this.opts.sdkVersion
    };
    let sentRuntimeSlot = false;
    if (this.playerToken !== null) {
      payload.ClientToken = this.opts.gameAccessToken;
      payload.Authorization = `Bearer ${this.playerToken}`;
    } else if (!this.runtimeSlotBlocked && this.opts.gameAccessToken.startsWith("pk_")) {
      payload.ClientToken = this.opts.gameAccessToken;
      sentRuntimeSlot = true;
    } else if (!this.runtimeSlotBlocked && this.opts.gameAccessToken.startsWith("sk_")) {
      payload.Authorization = `Bearer ${this.opts.gameAccessToken}`;
      sentRuntimeSlot = true;
    }
    const resp = await this.request(
      Commands.HandshakeRequest,
      payload,
      { responseCommand: Commands.HandshakeResponse }
    );
    if (resp.success === void 0) {
      throw new ProtocolError(
        "HandshakeResponse missing required `success` field",
        resp
      );
    }
    if (resp.success === true) return;
    const errorCode = typeof resp.errorCode === "number" ? resp.errorCode : 0;
    const errorMessage = resp.errorMessage ?? "Handshake failed";
    if (errorCode === 1 && sentRuntimeSlot && this.playerToken === null && !this.runtimeSlotBlocked) {
      this.runtimeSlotBlocked = true;
      this.log.warn(
        "handshake rejected with the runtime-credential slot \u2014 platform build lag; retrying legacy caller-less (runtime data plane degraded)"
      );
      await this.doHandshake();
      return;
    }
    const details = {};
    if (resp.server_version !== void 0) details.server_version = resp.server_version;
    if (resp.reason !== void 0) details.reason = resp.reason;
    if (FATAL_SESSION_CODES.has(errorCode)) {
      this.session._transitionTo("banned", {
        errorCode,
        errorMessage,
        ...details
      });
    }
    throw classifyHandshakeErrorCode(errorCode, errorMessage, details);
  }
  /**
   * TODO(PSV-1271 M3.5-Revert): fire-and-forget workaround. Backend's
   * `Core.Bus/DefaultCommandResolver.cs:13-20` routes the bare command name
   * `"ClientSettingsRequest"` to MQTT topic `"ClientSettingsRequest"`, which
   * no module subscribes to — Module.Auth listens on `"module_auth"`. The
   * request reaches the broker, the broker drops it, no response ever comes
   * back. See `docs/BACKEND_DEV_DIFF.md`.
   *
   * Tanks (`PlayServClient.js:101-104`) papers over this by sending and
   * proceeding without awaiting. We do the same: send via transport
   * directly (not through `dispatcher.request` — that would create a pending
   * entry that times out), echo the requested settings as if accepted, and
   * let the caller drive the FSM forward.
   *
   * Restore await + name-match correlation when backend ships the resolver
   * fix or confirms a wire-format prefix convention.
   */
  doClientSettings() {
    const requested = this.opts.allowMultipleConnections;
    const msg = {
      Command: prefixBusCommand(Commands.ClientSettingsRequest),
      Payload: { allowMultipleConnections: requested, RequestId: -1 }
    };
    this.transport.send(msg);
    return { allowMultipleConnections: requested };
  }
  handleSystemEvent(msg) {
    if (msg.Event === "SessionStateEvent") {
      const payload = msg.Payload;
      if (payload.State === "Banned") {
        this.session._transitionTo("banned", payload.Details);
        void this.close();
        return;
      }
      this.log.debug("SessionStateEvent (non-banned) ignored", payload);
      return;
    }
    this.router.dispatchSystemEvent(msg);
  }
};

// src/core/config.ts
var SDK_VERSION = "0.1.0";
function resolveConfig(cfg) {
  if (!cfg.endpoint) throw new InvalidArgumentError("config.endpoint is required");
  if (!cfg.gameAccessToken)
    throw new InvalidArgumentError("config.gameAccessToken is required");
  if (!cfg.gameId) throw new InvalidArgumentError("config.gameId is required");
  if (!cfg.userId) throw new InvalidArgumentError("config.userId is required");
  return {
    endpoint: cfg.endpoint,
    ...cfg.httpEndpoint !== void 0 ? { httpEndpoint: cfg.httpEndpoint } : {},
    gameAccessToken: cfg.gameAccessToken,
    gameId: cfg.gameId,
    ...cfg.projectId !== void 0 ? { projectId: cfg.projectId } : {},
    userId: cfg.userId,
    gameVersion: cfg.gameVersion ?? "0.0.0",
    sdkVersion: cfg.sdkVersion ?? SDK_VERSION,
    allowMultipleConnections: cfg.allowMultipleConnections ?? false,
    keepAliveIntervalMs: cfg.keepAliveIntervalMs ?? 3e4,
    storage: cfg.storage ?? defaultStorage(),
    logger: cfg.logger ?? new Logger({ prefix: "playserv" }),
    reconnect: cfg.reconnect ?? {},
    defaultTimeoutMs: cfg.defaultTimeoutMs ?? 1e4
  };
}

// src/events/EventBus.ts
var EventBus = class {
  constructor(connection, logger) {
    /** Per-`EventType` listener registry — the single delivery path. */
    this.listeners = /* @__PURE__ */ new Map();
    this.log = logger ?? new Logger({ prefix: "playserv:events" });
    connection.onEventMessage((eventType, data) => this.dispatch(eventType, data));
    connection.onRehydrate(() => this.rehydrate());
  }
  /**
   * Subscribe to events of `eventType`. Match is an exact string against the
   * `EventType` the server emits (typically the payload type's fully-qualified
   * name). Returns an idempotent `Subscription`; the last listener for an
   * `EventType` fires the `unsubscribeRemote` seam.
   */
  subscribe(eventType, callback) {
    const listener = callback;
    let set = this.listeners.get(eventType);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.listeners.set(eventType, set);
      this.subscribeRemote(eventType);
    }
    set.add(listener);
    const boundSet = set;
    return createSubscription(() => {
      boundSet.delete(listener);
      if (boundSet.size === 0) {
        this.listeners.delete(eventType);
        this.unsubscribeRemote(eventType);
      }
    });
  }
  /**
   * Seam retained for forward-compat / consumer override. The platform gates
   * delivery by group membership (`SubscribeGroupRequest`), not per-`EventType`
   * subscription (`EventSubscribeRequest` is an advisory no-op stub server-side),
   * so the SDK does not issue a per-EventType subscribe by default.
   */
  subscribeRemote(_eventType) {
  }
  unsubscribeRemote(_eventType) {
  }
  /** @internal visible for tests — inject a delivery for an `EventType`. */
  _dispatchForTest(eventType, data) {
    this.dispatch(eventType, data);
  }
  dispatch(eventType, data) {
    const set = this.listeners.get(eventType);
    if (!set || set.size === 0) return;
    for (const listener of Array.from(set)) {
      try {
        listener(data);
      } catch (err) {
        this.log.error(`listener for "${eventType}" threw`, err);
      }
    }
  }
  /**
   * Re-fire the `subscribeRemote` seam for each known `EventType` after a
   * successful re-handshake. No-op today (delivery is group-gated and group
   * membership is rehydrated by `PlayServClient`), but the seam is retained for
   * consumer overrides and asserted by tests.
   */
  rehydrate() {
    for (const eventType of this.listeners.keys()) {
      this.subscribeRemote(eventType);
    }
  }
  /**
   * Diagnostic snapshot (CODE_REVIEW.md §3.4). Useful for spotting unstopped
   * subscriptions when a scene tears down.
   */
  get debugSnapshot() {
    const topics = [];
    let listenerCount = 0;
    for (const [name, set] of this.listeners) {
      topics.push({ name, listeners: set.size });
      listenerCount += set.size;
    }
    return { topicCount: this.listeners.size, listenerCount, topics };
  }
};

// src/rpc/RpcClient.ts
var PROXY_PASSTHROUGH = /* @__PURE__ */ new Set([
  "then",
  "catch",
  "finally",
  "toString",
  "toJSON",
  "valueOf",
  "constructor",
  "prototype"
]);
function safeKey(args) {
  try {
    return JSON.stringify(args);
  } catch {
    return args.map((a) => String(a)).join("");
  }
}
var RpcClient = class {
  constructor(connection, log, opts = {}) {
    this.connection = connection;
    this.log = log;
    this.timeoutMs = opts.timeoutMs ?? 1e4;
  }
  /**
   * Build a Proxy that dispatches `proxy.method(...args)` as an RPC call.
   * `moduleName` is the camelCase server file name (e.g. `'domain'`, `'gameLogic'`).
   *
   * Pass `opts.dedupe` to coalesce concurrent calls with identical args
   * (§3.6). Only safe for idempotent methods.
   */
  remote(moduleName, opts = {}) {
    if (!moduleName) throw new Error("moduleName required");
    const target = /* @__PURE__ */ Object.create(null);
    const dedupeAll = opts.dedupe === "all";
    const dedupeSet = dedupeAll ? null : new Set(opts.dedupe ?? []);
    const decoders = opts.decoders ?? {};
    const inFlight = /* @__PURE__ */ new Map();
    const oneWayAll = opts.oneWay === "all";
    const oneWaySet = oneWayAll ? null : new Set(opts.oneWay ?? []);
    const isOneWay = (method) => oneWayAll || oneWaySet !== null && oneWaySet.has(method);
    if (opts.oneWay !== void 0) {
      for (const m of Object.keys(decoders)) {
        if (isOneWay(m)) {
          throw new Error(`remote('${moduleName}'): method '${m}' cannot be both oneWay and decoded`);
        }
      }
      if (opts.dedupe !== void 0) {
        const dedupeList = dedupeAll ? ["all"] : [...opts.dedupe ?? []];
        const conflict = dedupeAll ? opts.oneWay === "all" || (opts.oneWay?.length ?? 0) > 0 : dedupeList.some((m) => isOneWay(m));
        if (conflict) {
          throw new Error(`remote('${moduleName}'): oneWay methods cannot be deduped`);
        }
      }
    }
    const shouldDedupe = (method) => {
      if (dedupeAll) return true;
      return dedupeSet !== null && dedupeSet.has(method);
    };
    return new Proxy(target, {
      get: (_t, prop) => {
        if (typeof prop !== "string") return void 0;
        if (PROXY_PASSTHROUGH.has(prop)) return void 0;
        if (isOneWay(prop)) {
          return (...args) => this.callOneWay(moduleName, prop, args);
        }
        return (...args) => {
          const rawInvoke = () => {
            if (!shouldDedupe(prop)) {
              return this.call(moduleName, prop, args);
            }
            const key = `${prop}:${safeKey(args)}`;
            const existing = inFlight.get(key);
            if (existing !== void 0) return existing;
            const fresh = this.call(moduleName, prop, args);
            inFlight.set(key, fresh);
            void fresh.finally(() => {
              if (inFlight.get(key) === fresh) inFlight.delete(key);
            });
            return fresh;
          };
          const decode = Object.prototype.hasOwnProperty.call(decoders, prop) ? decoders[prop] : void 0;
          if (decode === void 0) return rawInvoke();
          return rawInvoke().then((raw) => this.applyDecoder(moduleName, prop, raw, decode));
        };
      }
    });
  }
  /**
   * Explicit typed call (B5). Invokes `service.method(...params)` and runs the
   * raw `Result` through `decode`, rejecting with `ContractMismatchError` if
   * the decoder throws. The reachable-without-a-Proxy counterpart to
   * `remote(name, { decoders })`.
   */
  async callTyped(service, method, params, decode) {
    const raw = await this.call(service, method, params);
    return this.applyDecoder(service, method, raw, decode);
  }
  /** Run a decoder, converting any thrown error into `ContractMismatchError`. */
  applyDecoder(service, method, raw, decode) {
    try {
      return decode(raw);
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      throw new ContractMismatchError(service, method, reason, raw);
    }
  }
  async call(service, method, params) {
    try {
      const resp = await this.connection.request(
        Commands.InvokeRpc,
        { ServiceName: service, MethodName: method, Payload: encodeRpcPayload(params) },
        { timeoutMs: this.timeoutMs, responseCommand: Commands.InvokeRpcResponse }
      );
      const status = typeof resp.Status === "string" ? resp.Status.toLowerCase() : "ok";
      if (status !== "ok") {
        const message = typeof resp.Message === "string" && resp.Message.length > 0 ? resp.Message : `RPC ${service}.${method} failed (${status})`;
        throw new RpcExecutionError({ Code: 0, Message: message, Details: { status } });
      }
      return decodeRpcResult(resp.Result);
    } catch (err) {
      throw this.wrapError(err, service, method);
    }
  }
  /**
   * One-way `FireAndForget` invoke — no pending entry, no timer, no
   * response. Resolves as soon as the frame is handed to the transport;
   * rejects (with the same error shaping as `call`) when the transport is
   * not open. The platform's `InvokeRpcHandler` one-way branch forwards it
   * over the game_server uplink and returns nothing to the client.
   */
  callOneWay(service, method, params) {
    try {
      this.connection.notify(Commands.InvokeRpc, {
        ServiceName: service,
        MethodName: method,
        Payload: encodeRpcPayload(params),
        FireAndForget: true
      });
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(this.wrapError(err, service, method));
    }
  }
  wrapError(err, service, method) {
    if (err instanceof TimeoutError) {
      return new TimeoutError(`RPC timeout: ${service}.${method}`);
    }
    if (err instanceof Error) return err;
    this.log.warn("wrapError: non-Error thrown", err);
    return new Error(String(err));
  }
};
function encodeRpcPayload(params) {
  const json = JSON.stringify(params);
  const bytes = new TextEncoder().encode(json);
  const g = globalThis;
  if (typeof g.Buffer !== "undefined") {
    return g.Buffer.from(bytes).toString("base64");
  }
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  if (typeof g.btoa === "function") return g.btoa(binary);
  throw new Error("No base64 encoder available (need Buffer or btoa)");
}
function decodeRpcResult(result) {
  if (typeof result !== "string") return result;
  try {
    return JSON.parse(result);
  } catch {
    return result;
  }
}

// src/data/QuerySerializer.ts
var KNOWN_OPERATORS = /* @__PURE__ */ new Set([
  "_eq",
  "_neq",
  "_gt",
  "_gte",
  "_lt",
  "_lte"
]);
function serializeQuery(ctx) {
  const variables = {};
  let counter = 0;
  const mkVar = (value) => {
    counter += 1;
    const name = `v${String(counter)}`;
    variables[name] = value;
    return `$${name}`;
  };
  const args = [];
  if (ctx.key !== void 0) {
    args.push(`id: ${mkVar(ctx.key)}`);
  }
  if (ctx.whereConditions.length > 0) {
    const merged = mergeConditions(ctx.whereConditions);
    args.push(`where: { ${serializeCondition(merged, mkVar)} }`);
  }
  const fieldsList = ctx.fields.length > 0 ? ctx.fields : ["Id"];
  const withBlocks = ctx.withClauses.map((w) => {
    const subArgs = [];
    if (w.condition) {
      subArgs.push(`where: { ${serializeCondition(w.condition, mkVar)} }`);
    }
    const subFields = w.fields && w.fields.length > 0 ? w.fields.join(" ") : "Id";
    const argsStr2 = subArgs.length > 0 ? `(${subArgs.join(", ")})` : "";
    return `${w.relation}${argsStr2} { ${subFields} }`;
  });
  const argsStr = args.length > 0 ? `(${args.join(", ")})` : "";
  const innerBody = [fieldsList.join(" "), ...withBlocks].join(" ").trim();
  const query = `${ctx.modelName}${argsStr} { ${innerBody} }`;
  return { Query: query, Variables: variables };
}
function serializeCondition(cond, mkVar) {
  const parts = [];
  for (const [field2, ops] of Object.entries(cond)) {
    const opParts = [];
    for (const [op, val] of Object.entries(ops)) {
      if (!KNOWN_OPERATORS.has(op)) {
        throw new Error(`Unknown operator in condition: ${op}`);
      }
      opParts.push(`${op}: ${mkVar(val)}`);
    }
    parts.push(`${field2}: { ${opParts.join(", ")} }`);
  }
  return parts.join(", ");
}
function mergeConditions(conds) {
  const merged = {};
  for (const c of conds) {
    for (const [field2, ops] of Object.entries(c)) {
      merged[field2] = { ...merged[field2] ?? {}, ...ops };
    }
  }
  return merged;
}

// node_modules/fast-json-patch/module/core.mjs
var core_exports = {};
__export(core_exports, {
  JsonPatchError: () => JsonPatchError,
  _areEquals: () => _areEquals,
  applyOperation: () => applyOperation,
  applyPatch: () => applyPatch,
  applyReducer: () => applyReducer,
  deepClone: () => deepClone,
  getValueByPointer: () => getValueByPointer,
  validate: () => validate,
  validator: () => validator
});

// node_modules/fast-json-patch/module/helpers.mjs
var __extends = /* @__PURE__ */ (function() {
  var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d2, b2) {
      d2.__proto__ = b2;
    } || function(d2, b2) {
      for (var p in b2) if (b2.hasOwnProperty(p)) d2[p] = b2[p];
    };
    return extendStatics(d, b);
  };
  return function(d, b) {
    extendStatics(d, b);
    function __() {
      this.constructor = d;
    }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
})();
var _hasOwnProperty = Object.prototype.hasOwnProperty;
function hasOwnProperty(obj, key) {
  return _hasOwnProperty.call(obj, key);
}
function _objectKeys(obj) {
  if (Array.isArray(obj)) {
    var keys_1 = new Array(obj.length);
    for (var k = 0; k < keys_1.length; k++) {
      keys_1[k] = "" + k;
    }
    return keys_1;
  }
  if (Object.keys) {
    return Object.keys(obj);
  }
  var keys = [];
  for (var i in obj) {
    if (hasOwnProperty(obj, i)) {
      keys.push(i);
    }
  }
  return keys;
}
function _deepClone(obj) {
  switch (typeof obj) {
    case "object":
      return JSON.parse(JSON.stringify(obj));
    //Faster than ES5 clone - http://jsperf.com/deep-cloning-of-objects/5
    case "undefined":
      return null;
    //this is how JSON.stringify behaves for array items
    default:
      return obj;
  }
}
function isInteger(str2) {
  var i = 0;
  var len = str2.length;
  var charCode;
  while (i < len) {
    charCode = str2.charCodeAt(i);
    if (charCode >= 48 && charCode <= 57) {
      i++;
      continue;
    }
    return false;
  }
  return true;
}
function escapePathComponent(path) {
  if (path.indexOf("/") === -1 && path.indexOf("~") === -1)
    return path;
  return path.replace(/~/g, "~0").replace(/\//g, "~1");
}
function unescapePathComponent(path) {
  return path.replace(/~1/g, "/").replace(/~0/g, "~");
}
function hasUndefined(obj) {
  if (obj === void 0) {
    return true;
  }
  if (obj) {
    if (Array.isArray(obj)) {
      for (var i_1 = 0, len = obj.length; i_1 < len; i_1++) {
        if (hasUndefined(obj[i_1])) {
          return true;
        }
      }
    } else if (typeof obj === "object") {
      var objKeys = _objectKeys(obj);
      var objKeysLength = objKeys.length;
      for (var i = 0; i < objKeysLength; i++) {
        if (hasUndefined(obj[objKeys[i]])) {
          return true;
        }
      }
    }
  }
  return false;
}
function patchErrorMessageFormatter(message, args) {
  var messageParts = [message];
  for (var key in args) {
    var value = typeof args[key] === "object" ? JSON.stringify(args[key], null, 2) : args[key];
    if (typeof value !== "undefined") {
      messageParts.push(key + ": " + value);
    }
  }
  return messageParts.join("\n");
}
var PatchError = (
  /** @class */
  (function(_super) {
    __extends(PatchError2, _super);
    function PatchError2(message, name, index, operation, tree) {
      var _newTarget = this.constructor;
      var _this = _super.call(this, patchErrorMessageFormatter(message, { name, index, operation, tree })) || this;
      _this.name = name;
      _this.index = index;
      _this.operation = operation;
      _this.tree = tree;
      Object.setPrototypeOf(_this, _newTarget.prototype);
      _this.message = patchErrorMessageFormatter(message, { name, index, operation, tree });
      return _this;
    }
    return PatchError2;
  })(Error)
);

// node_modules/fast-json-patch/module/core.mjs
var JsonPatchError = PatchError;
var deepClone = _deepClone;
var objOps = {
  add: function(obj, key, document) {
    obj[key] = this.value;
    return { newDocument: document };
  },
  remove: function(obj, key, document) {
    var removed = obj[key];
    delete obj[key];
    return { newDocument: document, removed };
  },
  replace: function(obj, key, document) {
    var removed = obj[key];
    obj[key] = this.value;
    return { newDocument: document, removed };
  },
  move: function(obj, key, document) {
    var removed = getValueByPointer(document, this.path);
    if (removed) {
      removed = _deepClone(removed);
    }
    var originalValue = applyOperation(document, { op: "remove", path: this.from }).removed;
    applyOperation(document, { op: "add", path: this.path, value: originalValue });
    return { newDocument: document, removed };
  },
  copy: function(obj, key, document) {
    var valueToCopy = getValueByPointer(document, this.from);
    applyOperation(document, { op: "add", path: this.path, value: _deepClone(valueToCopy) });
    return { newDocument: document };
  },
  test: function(obj, key, document) {
    return { newDocument: document, test: _areEquals(obj[key], this.value) };
  },
  _get: function(obj, key, document) {
    this.value = obj[key];
    return { newDocument: document };
  }
};
var arrOps = {
  add: function(arr, i, document) {
    if (isInteger(i)) {
      arr.splice(i, 0, this.value);
    } else {
      arr[i] = this.value;
    }
    return { newDocument: document, index: i };
  },
  remove: function(arr, i, document) {
    var removedList = arr.splice(i, 1);
    return { newDocument: document, removed: removedList[0] };
  },
  replace: function(arr, i, document) {
    var removed = arr[i];
    arr[i] = this.value;
    return { newDocument: document, removed };
  },
  move: objOps.move,
  copy: objOps.copy,
  test: objOps.test,
  _get: objOps._get
};
function getValueByPointer(document, pointer) {
  if (pointer == "") {
    return document;
  }
  var getOriginalDestination = { op: "_get", path: pointer };
  applyOperation(document, getOriginalDestination);
  return getOriginalDestination.value;
}
function applyOperation(document, operation, validateOperation, mutateDocument, banPrototypeModifications, index) {
  if (validateOperation === void 0) {
    validateOperation = false;
  }
  if (mutateDocument === void 0) {
    mutateDocument = true;
  }
  if (banPrototypeModifications === void 0) {
    banPrototypeModifications = true;
  }
  if (index === void 0) {
    index = 0;
  }
  if (validateOperation) {
    if (typeof validateOperation == "function") {
      validateOperation(operation, 0, document, operation.path);
    } else {
      validator(operation, 0);
    }
  }
  if (operation.path === "") {
    var returnValue = { newDocument: document };
    if (operation.op === "add") {
      returnValue.newDocument = operation.value;
      return returnValue;
    } else if (operation.op === "replace") {
      returnValue.newDocument = operation.value;
      returnValue.removed = document;
      return returnValue;
    } else if (operation.op === "move" || operation.op === "copy") {
      returnValue.newDocument = getValueByPointer(document, operation.from);
      if (operation.op === "move") {
        returnValue.removed = document;
      }
      return returnValue;
    } else if (operation.op === "test") {
      returnValue.test = _areEquals(document, operation.value);
      if (returnValue.test === false) {
        throw new JsonPatchError("Test operation failed", "TEST_OPERATION_FAILED", index, operation, document);
      }
      returnValue.newDocument = document;
      return returnValue;
    } else if (operation.op === "remove") {
      returnValue.removed = document;
      returnValue.newDocument = null;
      return returnValue;
    } else if (operation.op === "_get") {
      operation.value = document;
      return returnValue;
    } else {
      if (validateOperation) {
        throw new JsonPatchError("Operation `op` property is not one of operations defined in RFC-6902", "OPERATION_OP_INVALID", index, operation, document);
      } else {
        return returnValue;
      }
    }
  } else {
    if (!mutateDocument) {
      document = _deepClone(document);
    }
    var path = operation.path || "";
    var keys = path.split("/");
    var obj = document;
    var t = 1;
    var len = keys.length;
    var existingPathFragment = void 0;
    var key = void 0;
    var validateFunction = void 0;
    if (typeof validateOperation == "function") {
      validateFunction = validateOperation;
    } else {
      validateFunction = validator;
    }
    while (true) {
      key = keys[t];
      if (key && key.indexOf("~") != -1) {
        key = unescapePathComponent(key);
      }
      if (banPrototypeModifications && (key == "__proto__" || key == "prototype" && t > 0 && keys[t - 1] == "constructor")) {
        throw new TypeError("JSON-Patch: modifying `__proto__` or `constructor/prototype` prop is banned for security reasons, if this was on purpose, please set `banPrototypeModifications` flag false and pass it to this function. More info in fast-json-patch README");
      }
      if (validateOperation) {
        if (existingPathFragment === void 0) {
          if (obj[key] === void 0) {
            existingPathFragment = keys.slice(0, t).join("/");
          } else if (t == len - 1) {
            existingPathFragment = operation.path;
          }
          if (existingPathFragment !== void 0) {
            validateFunction(operation, 0, document, existingPathFragment);
          }
        }
      }
      t++;
      if (Array.isArray(obj)) {
        if (key === "-") {
          key = obj.length;
        } else {
          if (validateOperation && !isInteger(key)) {
            throw new JsonPatchError("Expected an unsigned base-10 integer value, making the new referenced value the array element with the zero-based index", "OPERATION_PATH_ILLEGAL_ARRAY_INDEX", index, operation, document);
          } else if (isInteger(key)) {
            key = ~~key;
          }
        }
        if (t >= len) {
          if (validateOperation && operation.op === "add" && key > obj.length) {
            throw new JsonPatchError("The specified index MUST NOT be greater than the number of elements in the array", "OPERATION_VALUE_OUT_OF_BOUNDS", index, operation, document);
          }
          var returnValue = arrOps[operation.op].call(operation, obj, key, document);
          if (returnValue.test === false) {
            throw new JsonPatchError("Test operation failed", "TEST_OPERATION_FAILED", index, operation, document);
          }
          return returnValue;
        }
      } else {
        if (t >= len) {
          var returnValue = objOps[operation.op].call(operation, obj, key, document);
          if (returnValue.test === false) {
            throw new JsonPatchError("Test operation failed", "TEST_OPERATION_FAILED", index, operation, document);
          }
          return returnValue;
        }
      }
      obj = obj[key];
      if (validateOperation && t < len && (!obj || typeof obj !== "object")) {
        throw new JsonPatchError("Cannot perform operation at the desired path", "OPERATION_PATH_UNRESOLVABLE", index, operation, document);
      }
    }
  }
}
function applyPatch(document, patch, validateOperation, mutateDocument, banPrototypeModifications) {
  if (mutateDocument === void 0) {
    mutateDocument = true;
  }
  if (banPrototypeModifications === void 0) {
    banPrototypeModifications = true;
  }
  if (validateOperation) {
    if (!Array.isArray(patch)) {
      throw new JsonPatchError("Patch sequence must be an array", "SEQUENCE_NOT_AN_ARRAY");
    }
  }
  if (!mutateDocument) {
    document = _deepClone(document);
  }
  var results = new Array(patch.length);
  for (var i = 0, length_1 = patch.length; i < length_1; i++) {
    results[i] = applyOperation(document, patch[i], validateOperation, true, banPrototypeModifications, i);
    document = results[i].newDocument;
  }
  results.newDocument = document;
  return results;
}
function applyReducer(document, operation, index) {
  var operationResult = applyOperation(document, operation);
  if (operationResult.test === false) {
    throw new JsonPatchError("Test operation failed", "TEST_OPERATION_FAILED", index, operation, document);
  }
  return operationResult.newDocument;
}
function validator(operation, index, document, existingPathFragment) {
  if (typeof operation !== "object" || operation === null || Array.isArray(operation)) {
    throw new JsonPatchError("Operation is not an object", "OPERATION_NOT_AN_OBJECT", index, operation, document);
  } else if (!objOps[operation.op]) {
    throw new JsonPatchError("Operation `op` property is not one of operations defined in RFC-6902", "OPERATION_OP_INVALID", index, operation, document);
  } else if (typeof operation.path !== "string") {
    throw new JsonPatchError("Operation `path` property is not a string", "OPERATION_PATH_INVALID", index, operation, document);
  } else if (operation.path.indexOf("/") !== 0 && operation.path.length > 0) {
    throw new JsonPatchError('Operation `path` property must start with "/"', "OPERATION_PATH_INVALID", index, operation, document);
  } else if ((operation.op === "move" || operation.op === "copy") && typeof operation.from !== "string") {
    throw new JsonPatchError("Operation `from` property is not present (applicable in `move` and `copy` operations)", "OPERATION_FROM_REQUIRED", index, operation, document);
  } else if ((operation.op === "add" || operation.op === "replace" || operation.op === "test") && operation.value === void 0) {
    throw new JsonPatchError("Operation `value` property is not present (applicable in `add`, `replace` and `test` operations)", "OPERATION_VALUE_REQUIRED", index, operation, document);
  } else if ((operation.op === "add" || operation.op === "replace" || operation.op === "test") && hasUndefined(operation.value)) {
    throw new JsonPatchError("Operation `value` property is not present (applicable in `add`, `replace` and `test` operations)", "OPERATION_VALUE_CANNOT_CONTAIN_UNDEFINED", index, operation, document);
  } else if (document) {
    if (operation.op == "add") {
      var pathLen = operation.path.split("/").length;
      var existingPathLen = existingPathFragment.split("/").length;
      if (pathLen !== existingPathLen + 1 && pathLen !== existingPathLen) {
        throw new JsonPatchError("Cannot perform an `add` operation at the desired path", "OPERATION_PATH_CANNOT_ADD", index, operation, document);
      }
    } else if (operation.op === "replace" || operation.op === "remove" || operation.op === "_get") {
      if (operation.path !== existingPathFragment) {
        throw new JsonPatchError("Cannot perform the operation at a path that does not exist", "OPERATION_PATH_UNRESOLVABLE", index, operation, document);
      }
    } else if (operation.op === "move" || operation.op === "copy") {
      var existingValue = { op: "_get", path: operation.from, value: void 0 };
      var error = validate([existingValue], document);
      if (error && error.name === "OPERATION_PATH_UNRESOLVABLE") {
        throw new JsonPatchError("Cannot perform the operation from a path that does not exist", "OPERATION_FROM_UNRESOLVABLE", index, operation, document);
      }
    }
  }
}
function validate(sequence, document, externalValidator) {
  try {
    if (!Array.isArray(sequence)) {
      throw new JsonPatchError("Patch sequence must be an array", "SEQUENCE_NOT_AN_ARRAY");
    }
    if (document) {
      applyPatch(_deepClone(document), _deepClone(sequence), externalValidator || true);
    } else {
      externalValidator = externalValidator || validator;
      for (var i = 0; i < sequence.length; i++) {
        externalValidator(sequence[i], i, document, void 0);
      }
    }
  } catch (e) {
    if (e instanceof JsonPatchError) {
      return e;
    } else {
      throw e;
    }
  }
}
function _areEquals(a, b) {
  if (a === b)
    return true;
  if (a && b && typeof a == "object" && typeof b == "object") {
    var arrA = Array.isArray(a), arrB = Array.isArray(b), i, length, key;
    if (arrA && arrB) {
      length = a.length;
      if (length != b.length)
        return false;
      for (i = length; i-- !== 0; )
        if (!_areEquals(a[i], b[i]))
          return false;
      return true;
    }
    if (arrA != arrB)
      return false;
    var keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length)
      return false;
    for (i = length; i-- !== 0; )
      if (!b.hasOwnProperty(keys[i]))
        return false;
    for (i = length; i-- !== 0; ) {
      key = keys[i];
      if (!_areEquals(a[key], b[key]))
        return false;
    }
    return true;
  }
  return a !== a && b !== b;
}

// node_modules/fast-json-patch/module/duplex.mjs
var duplex_exports = {};
__export(duplex_exports, {
  compare: () => compare,
  generate: () => generate,
  observe: () => observe,
  unobserve: () => unobserve
});
var beforeDict = /* @__PURE__ */ new WeakMap();
var Mirror = (
  /** @class */
  /* @__PURE__ */ (function() {
    function Mirror2(obj) {
      this.observers = /* @__PURE__ */ new Map();
      this.obj = obj;
    }
    return Mirror2;
  })()
);
var ObserverInfo = (
  /** @class */
  /* @__PURE__ */ (function() {
    function ObserverInfo2(callback, observer) {
      this.callback = callback;
      this.observer = observer;
    }
    return ObserverInfo2;
  })()
);
function getMirror(obj) {
  return beforeDict.get(obj);
}
function getObserverFromMirror(mirror, callback) {
  return mirror.observers.get(callback);
}
function removeObserverFromMirror(mirror, observer) {
  mirror.observers.delete(observer.callback);
}
function unobserve(root, observer) {
  observer.unobserve();
}
function observe(obj, callback) {
  var patches = [];
  var observer;
  var mirror = getMirror(obj);
  if (!mirror) {
    mirror = new Mirror(obj);
    beforeDict.set(obj, mirror);
  } else {
    var observerInfo = getObserverFromMirror(mirror, callback);
    observer = observerInfo && observerInfo.observer;
  }
  if (observer) {
    return observer;
  }
  observer = {};
  mirror.value = _deepClone(obj);
  if (callback) {
    observer.callback = callback;
    observer.next = null;
    var dirtyCheck = function() {
      generate(observer);
    };
    var fastCheck = function() {
      clearTimeout(observer.next);
      observer.next = setTimeout(dirtyCheck);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("mouseup", fastCheck);
      window.addEventListener("keyup", fastCheck);
      window.addEventListener("mousedown", fastCheck);
      window.addEventListener("keydown", fastCheck);
      window.addEventListener("change", fastCheck);
    }
  }
  observer.patches = patches;
  observer.object = obj;
  observer.unobserve = function() {
    generate(observer);
    clearTimeout(observer.next);
    removeObserverFromMirror(mirror, observer);
    if (typeof window !== "undefined") {
      window.removeEventListener("mouseup", fastCheck);
      window.removeEventListener("keyup", fastCheck);
      window.removeEventListener("mousedown", fastCheck);
      window.removeEventListener("keydown", fastCheck);
      window.removeEventListener("change", fastCheck);
    }
  };
  mirror.observers.set(callback, new ObserverInfo(callback, observer));
  return observer;
}
function generate(observer, invertible) {
  if (invertible === void 0) {
    invertible = false;
  }
  var mirror = beforeDict.get(observer.object);
  _generate(mirror.value, observer.object, observer.patches, "", invertible);
  if (observer.patches.length) {
    applyPatch(mirror.value, observer.patches);
  }
  var temp = observer.patches;
  if (temp.length > 0) {
    observer.patches = [];
    if (observer.callback) {
      observer.callback(temp);
    }
  }
  return temp;
}
function _generate(mirror, obj, patches, path, invertible) {
  if (obj === mirror) {
    return;
  }
  if (typeof obj.toJSON === "function") {
    obj = obj.toJSON();
  }
  var newKeys = _objectKeys(obj);
  var oldKeys = _objectKeys(mirror);
  var deleted = false;
  for (var t = oldKeys.length - 1; t >= 0; t--) {
    var key = oldKeys[t];
    var oldVal = mirror[key];
    if (hasOwnProperty(obj, key) && !(obj[key] === void 0 && oldVal !== void 0 && Array.isArray(obj) === false)) {
      var newVal = obj[key];
      if (typeof oldVal == "object" && oldVal != null && typeof newVal == "object" && newVal != null && Array.isArray(oldVal) === Array.isArray(newVal)) {
        _generate(oldVal, newVal, patches, path + "/" + escapePathComponent(key), invertible);
      } else {
        if (oldVal !== newVal) {
          if (invertible) {
            patches.push({ op: "test", path: path + "/" + escapePathComponent(key), value: _deepClone(oldVal) });
          }
          patches.push({ op: "replace", path: path + "/" + escapePathComponent(key), value: _deepClone(newVal) });
        }
      }
    } else if (Array.isArray(mirror) === Array.isArray(obj)) {
      if (invertible) {
        patches.push({ op: "test", path: path + "/" + escapePathComponent(key), value: _deepClone(oldVal) });
      }
      patches.push({ op: "remove", path: path + "/" + escapePathComponent(key) });
      deleted = true;
    } else {
      if (invertible) {
        patches.push({ op: "test", path, value: mirror });
      }
      patches.push({ op: "replace", path, value: obj });
    }
  }
  if (!deleted && newKeys.length == oldKeys.length) {
    return;
  }
  for (var t = 0; t < newKeys.length; t++) {
    var key = newKeys[t];
    if (!hasOwnProperty(mirror, key) && obj[key] !== void 0) {
      patches.push({ op: "add", path: path + "/" + escapePathComponent(key), value: _deepClone(obj[key]) });
    }
  }
}
function compare(tree1, tree2, invertible) {
  if (invertible === void 0) {
    invertible = false;
  }
  var patches = [];
  _generate(tree1, tree2, patches, "", invertible);
  return patches;
}

// node_modules/fast-json-patch/index.mjs
Object.assign({}, core_exports, duplex_exports, {
  JsonPatchError: PatchError,
  deepClone: _deepClone,
  escapePathComponent,
  unescapePathComponent
});

// src/data/SubscriptionCache.ts
var NO_OP_LOGGER = { warn: () => {
} };
function isJsonPatchOp(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value) && typeof value.op === "string";
}
var SubscriptionCache = class {
  constructor(log = NO_OP_LOGGER) {
    this.log = log;
    this._state = null;
  }
  get state() {
    return this._state;
  }
  /** True once the initial `Overwrite` has been applied. */
  get hasState() {
    return this._state !== null;
  }
  /**
   * Apply one server push and return the previous state (`null` on first apply).
   * Callers use the returned value for `event.old` in `'update'` listeners.
   */
  apply(update) {
    const prev = this._state;
    if (update.UpdateType === "Overwrite") {
      this._state = Array.isArray(update.Data) ? update.Data : update.Data;
      return prev;
    }
    const data = update.Data;
    const isArray = Array.isArray(data);
    const isSingleOp = !isArray && isJsonPatchOp(data);
    if (!isArray && !isSingleOp) {
      this.log.warn(
        "SubscriptionCache: UpdateType=Patch received plain object (no JSON Patch ops). Treating as Overwrite. Backend wire format may need clarification."
      );
      this._state = data;
      return prev;
    }
    if (prev === null) {
      throw new Error("SubscriptionCache: cannot apply Patch before initial Overwrite");
    }
    const ops = Array.isArray(data) ? data : [data];
    const result = applyPatch(prev, ops, false, false);
    this._state = result.newDocument;
    return prev;
  }
  reset() {
    this._state = null;
  }
};

// src/data/ListenHandle.ts
var ListenHandleImpl = class {
  constructor(connection, plan, log) {
    this.connection = connection;
    this.plan = plan;
    this.log = log;
    this.listeners = /* @__PURE__ */ new Set();
    this.bufferedFrames = [];
    this.requestId = null;
    this.subscriptionId = null;
    this.stopped = false;
    this.cache = new SubscriptionCache(log);
    this.offSystemEvent = connection.onSystemEvent("DataSubscriptionUpdate", (p) => {
      this.onUpdateFrame(p);
    });
    this.offRehydrate = connection.onRehydrate(() => this.resubscribe());
    void this.subscribe();
  }
  wait(event, callback) {
    if (event !== "update") {
      throw new InvalidArgumentError(`Unknown listen event: ${String(event)}`);
    }
    if (this.stopped) {
      throw new InvalidArgumentError("ListenHandle.wait(): handle already stopped");
    }
    this.listeners.add(callback);
    return createSubscription(() => {
      this.listeners.delete(callback);
    });
  }
  get() {
    return Promise.resolve(this.cache.state);
  }
  stop() {
    if (this.stopped) return Promise.resolve();
    this.stopped = true;
    this.offSystemEvent();
    this.offRehydrate();
    this.listeners.clear();
    this.subscriptionId = null;
    this.cache.reset();
    return Promise.resolve();
  }
  // ---------- Internal ----------
  async subscribe() {
    try {
      const { requestId, promise } = this.connection.requestWithId(Commands.DataSubscriptionRequest, {
        Query: this.plan.Query,
        Variables: this.plan.Variables
      });
      this.requestId = requestId;
      const resp = await promise;
      if (this.stopped) return;
      const sid = resp.Result?.SubscriptionId;
      if (typeof sid !== "number") {
        this.log.warn("DataSubscriptionResponse missing SubscriptionId", resp);
        return;
      }
      this.subscriptionId = sid;
      this.flushBuffered();
    } catch (err) {
      if (!this.stopped) this.log.warn("DataSubscriptionRequest failed", err);
    }
  }
  async resubscribe() {
    if (this.stopped) return;
    this.subscriptionId = null;
    this.requestId = null;
    this.bufferedFrames.length = 0;
    await this.subscribe();
  }
  onUpdateFrame(frame) {
    if (this.stopped) return;
    if (this.subscriptionId === null) {
      if (this.requestId !== null && frame.RequestId === this.requestId) {
        this.bufferedFrames.push(frame);
      }
      return;
    }
    if (frame.DataSubscriptionId !== this.subscriptionId) return;
    this.applyAndDispatch(frame);
  }
  flushBuffered() {
    for (const frame of this.bufferedFrames) {
      if (frame.DataSubscriptionId === this.subscriptionId || frame.RequestId === this.requestId) {
        this.applyAndDispatch(frame);
      }
    }
    this.bufferedFrames.length = 0;
  }
  applyAndDispatch(frame) {
    if (frame.UpdateType === void 0 || frame.Data === void 0) return;
    const update = {
      UpdateType: frame.UpdateType,
      Data: frame.Data
    };
    let prev;
    try {
      prev = this.cache.apply(update);
    } catch (err) {
      this.log.warn("subscription cache apply failed", err);
      return;
    }
    const next = this.cache.state;
    if (next === null) return;
    const event = { new: next, old: prev };
    for (const listener of Array.from(this.listeners)) {
      try {
        listener(event);
      } catch (err) {
        this.log.error("listen update listener threw", err);
      }
    }
  }
};

// src/data/QueryBuilder.ts
var BATCH_LIMIT = 500;
var QueryBuilder = class _QueryBuilder {
  constructor(connection, state) {
    this.connection = connection;
    this.state = state;
  }
  get modelName() {
    return this.state.modelName;
  }
  key(id) {
    return new _QueryBuilder(this.connection, { ...this.state, key: id });
  }
  where(condition) {
    return new _QueryBuilder(this.connection, {
      ...this.state,
      whereConditions: [...this.state.whereConditions, condition]
    });
  }
  with(relation, condition, fields) {
    const clause = {
      relation,
      ...condition !== void 0 ? { condition } : {},
      ...fields !== void 0 ? { fields } : {}
    };
    return new _QueryBuilder(this.connection, {
      ...this.state,
      withClauses: [...this.state.withClauses, clause]
    });
  }
  /**
   * Explicit field list — MVP surrogate for the analyser (decision #12b).
   * Without `.fields()` the query falls back to `{ Id }`, so the server at
   * least returns identity and the caller knows to refine the selection.
   */
  fields(...fieldNames) {
    return new _QueryBuilder(this.connection, { ...this.state, fields: fieldNames });
  }
  /**
   * Execute the query.
   * - After `.key()` → `T | null` (`null` when the server has no object).
   * - After `.where()` (no key) → `ModelCollection<T>` (possibly empty).
   * - Without `.key()` or `.where()` → throws `InvalidArgumentError`.
   */
  async get(opts = {}) {
    this.assertRunnable();
    const track = opts.trackAccess === true && this.state.fieldAccess !== void 0 && this.state.key !== void 0 && this.state.fields.length === 0;
    let sig;
    let fieldsOverride;
    if (track) {
      sig = this.signature();
      const cached = this.state.fieldAccess.cachedFields(sig);
      if (cached.length > 0) {
        fieldsOverride = cached.includes("Id") ? cached : ["Id", ...cached];
      }
    }
    const plan = this.buildPlan(fieldsOverride);
    const resp = await this.connection.request(Commands.DataGetRequest, { Query: plan.Query, Variables: plan.Variables });
    const raw = resp.Result?.Data?.[this.state.modelName];
    if (this.state.key !== void 0) {
      const obj = raw ?? null;
      if (track && obj !== null) {
        return this.state.fieldAccess.track(sig, obj);
      }
      return obj;
    }
    const list = Array.isArray(raw) ? raw : [];
    return this.makeCollection(list);
  }
  /**
   * Stable signature for auto Data Retrieval — the "which row(s)" identity of
   * the query (model + key + where + with), excluding the selected fields so a
   * narrowed re-fetch maps to the same entry.
   */
  signature() {
    return JSON.stringify({
      m: this.state.modelName,
      k: this.state.key ?? null,
      w: this.state.whereConditions,
      with: this.state.withClauses
    });
  }
  /**
   * PSV-1271 P7: entity deletion is NOT supported over the WS data path — the
   * platform routes no `DataDeleteRequest` (mutations are merge-upsert only), so
   * a delete would silently time out. Deletion is a server-side concern
   * (game-server logic / the runtime Data REST API). Throws `NotSupportedError`.
   */
  delete() {
    return Promise.reject(
      new NotSupportedError(
        "Entity deletion is not supported over the realtime WS path \u2014 the platform only upserts via model().save(). Delete server-side (an @server function / the runtime Data API)."
      )
    );
  }
  /**
   * Fetch + (optional) map + save.
   * - After `.key()` → 1 mutation.
   * - After `.where()` → up to 500 mutations in parallel (throws if over).
   *
   * `mapperFn` runs after the fetch, directly on each item, before sending.
   * Decision #11 reminder: per-item `Overwrite` with the object payload.
   */
  async update(mapperFn) {
    this.assertRunnable();
    const data = await this.get();
    if (data === null) return;
    if (Array.isArray(data)) {
      if (data.length > BATCH_LIMIT) {
        throw new InvalidArgumentError(
          `update() batch exceeds ${String(BATCH_LIMIT)}-item limit (got ${String(data.length)})`
        );
      }
      if (mapperFn) for (const item of data) mapperFn(item);
      await Promise.all(data.map((item) => this.mutateOne(item)));
      return;
    }
    if (mapperFn) mapperFn(data);
    await this.mutateOne(data);
  }
  /**
   * Load an entity by id, or hand back a fresh one to create (B3b).
   *
   * Mirrors skeldar SDK v0.2.0's `LoadOrCreate<T>` (PSV-1584): fetch by `id`;
   * if it exists, return it with `created: false`; if not, return a fresh
   * `{ Id: id, ... }` draft (optionally initialised via `init`) with
   * `created: true`. Like skeldar, it does **not** auto-persist — call the
   * returned `save()` to write.
   *
   * `save()` works for both branches because the WS-proxy `DataMutationRequest`
   * is upsert-based: a mutation against a non-existent key creates the entity
   * (confirmed in `playserv-backend` `RedisDataMutationExecutor` — no EXISTS
   * check; client `Overwrite` maps to an internal `Patch` so it never wipes).
   * That upsert behaviour is exactly what makes a no-prior-fetch create path
   * possible; the ordinary `update()` flow can't create because it re-fetches
   * first and short-circuits on `null`.
   *
   *   const { data, created, save } = await playServ.model('Player').loadOrCreate(id);
   *   if (created) data.Level = 1;
   *   await save();
   */
  async loadOrCreate(id, init) {
    if (typeof id !== "string" || id.length === 0) {
      throw new InvalidArgumentError("loadOrCreate(id) requires a non-empty string id");
    }
    const keyed = this.key(id);
    const existing = await keyed.get();
    if (existing !== null) {
      return { data: existing, created: false, save: () => keyed.mutateOne(existing) };
    }
    const draft = { Id: id };
    if (init) init(draft);
    return { data: draft, created: true, save: () => keyed.mutateOne(draft) };
  }
  /**
   * Create a new record at `id` (PSV-1344).
   *
   * Explicit and symmetric with `update()` / `delete()`; returns the created
   * record (with `Id`). Persists through the **same upsert `DataMutationRequest`
   * path** as `update()` / `loadOrCreate().save()` — there is no dedicated
   * `DataCreateRequest` on the wire (the WS-proxy / platform DataFlow upserts).
   *
   * Duplicate guard is **best-effort**: a pre-`get()` throws `DuplicateKeyError`
   * if `id` is already taken. The backend has no atomic create / optimistic
   * lock, so the check is not race-free — see `DuplicateKeyError`. A missing
   * required field surfaces as `ValidationError` from the mutation itself.
   */
  async create(id, data) {
    if (typeof id !== "string" || id.length === 0) {
      throw new InvalidArgumentError("create(id, data) requires a non-empty string id");
    }
    if (data === null || typeof data !== "object") {
      throw new InvalidArgumentError("create(id, data) requires a data object");
    }
    const keyed = this.key(id);
    if (await keyed.get() !== null) {
      throw new DuplicateKeyError(this.state.modelName, id);
    }
    const record = { ...data, Id: id };
    await keyed.mutateOne(record);
    return record;
  }
  /**
   * Subscribe to realtime changes of a single entity. Requires `.key()`.
   * Returns a handle with `wait('update', cb)`, `get()`, `stop()`.
   *
   * Collection subscription (`.where(...).listen()`) is an **accepted
   * limitation** (PSV-1347): the platform's data-subscription gateway only
   * supports single-entity-by-id subscriptions — the query grammar requires a
   * mandatory `id` and updates are full `Overwrite` snapshots with
   * `IsCollection` always `false`; filtered/collection subscriptions are
   * explicitly deferred server-side. Canonical workarounds until then:
   *   - small, known set → `listen()` per key;
   *   - dynamic collection → a server `@event` the runtime broadcasts on change.
   */
  listen() {
    if (this.state.key === void 0) {
      throw new InvalidArgumentError(
        "listen() requires .key(); collection subscription via .where().listen() is not supported (platform limitation, PSV-1347). Use per-key listen() for a known set, or a server @event broadcast for a dynamic collection."
      );
    }
    const plan = this.buildPlan();
    return new ListenHandleImpl(this.connection, plan, this.state.logger);
  }
  /** @internal — exposed for tests. */
  _serialize() {
    this.assertRunnable();
    return this.buildPlan();
  }
  // ---------- Internal ----------
  async mutateOne(item) {
    const record = item;
    const rawId = record.Id ?? record.id;
    if (typeof rawId !== "string" && typeof rawId !== "number") {
      throw new InvalidArgumentError("update() requires items to carry an Id/id field");
    }
    const idStr = String(rawId);
    const dataOut = { ...record };
    delete dataOut.Id;
    delete dataOut.id;
    const fieldsForMutation = this.state.fields.length > 0 ? this.state.fields : Object.keys(dataOut);
    const plan = serializeQuery({
      modelName: this.state.modelName,
      key: idStr,
      whereConditions: [],
      withClauses: [],
      fields: fieldsForMutation
    });
    await this.connection.request(Commands.DataMutationRequest, {
      Query: plan.Query,
      Variables: plan.Variables,
      UpdateType: "Overwrite",
      Data: dataOut
    });
  }
  buildPlan(fieldsOverride) {
    const ctx = {
      modelName: this.state.modelName,
      ...this.state.key !== void 0 ? { key: this.state.key } : {},
      whereConditions: this.state.whereConditions,
      withClauses: this.state.withClauses,
      fields: fieldsOverride ?? this.state.fields
    };
    return serializeQuery(ctx);
  }
  assertRunnable() {
    if (this.state.key === void 0 && this.state.whereConditions.length === 0) {
      throw new InvalidArgumentError("Query requires key() or where()");
    }
  }
  makeCollection(items) {
    const mutate = (item) => this.mutateOne(item);
    const runDelete = () => this.delete();
    const updateFn = async function(mapperFn) {
      if (items.length > BATCH_LIMIT) {
        throw new InvalidArgumentError(
          `update() batch exceeds ${String(BATCH_LIMIT)}-item limit (got ${String(items.length)})`
        );
      }
      if (mapperFn) for (const item of items) mapperFn(item);
      await Promise.all(items.map((item) => mutate(item)));
    };
    const deleteFn = runDelete;
    return Object.assign(items, { update: updateFn, delete: deleteFn });
  }
};

// src/analyzer/FieldTracker.ts
var IGNORED_PROPS = /* @__PURE__ */ new Set([
  // Node/JS internals — avoid poisoning the set with structural access.
  "constructor",
  "toJSON",
  "toString",
  "valueOf",
  "length",
  "then",
  Symbol.iterator,
  Symbol.toPrimitive,
  Symbol.toStringTag
]);
function createFieldTracker(data, accessed, opts = {}) {
  const join = opts.joinPath ?? ((segs) => segs.join("/"));
  return wrap(data, accessed, [], join);
}
function wrap(data, accessed, path, join) {
  if (data === null || typeof data !== "object") return data;
  return new Proxy(data, {
    get(obj, prop, receiver) {
      if (IGNORED_PROPS.has(prop)) {
        return Reflect.get(obj, prop, receiver);
      }
      const propStr = typeof prop === "symbol" ? prop.description ?? "" : String(prop);
      const nextPath = [...path, propStr];
      accessed.add(join(nextPath));
      const value = Reflect.get(obj, prop, receiver);
      if (value !== null && typeof value === "object") {
        return wrap(value, accessed, nextPath, join);
      }
      return value;
    }
  });
}

// src/data/FieldAccessRegistry.ts
var FieldAccessRegistry = class {
  constructor() {
    this.accessed = /* @__PURE__ */ new Map();
  }
  /**
   * Top-level field names recorded for `sig` (the first segment of each
   * tracked path, de-duplicated). Empty when nothing has been read yet — the
   * caller then leaves the query unnarrowed.
   */
  cachedFields(sig) {
    const set = this.accessed.get(sig);
    if (set === void 0 || set.size === 0) return [];
    const top = /* @__PURE__ */ new Set();
    for (const path of set) {
      const head = path.split("/")[0];
      if (head !== void 0 && head !== "") top.add(head);
    }
    return [...top];
  }
  /**
   * Wrap `obj` in a field-tracking Proxy bound to a fresh set for `sig`
   * (replacing any prior recording), so subsequent reads on the returned object
   * drive the next fetch's field selection.
   */
  track(sig, obj) {
    const set = /* @__PURE__ */ new Set();
    this.accessed.set(sig, set);
    return createFieldTracker(obj, set);
  }
};

// src/auth/AuthClient.ts
var RUNTIME = "/auth/players";
var LEGACY = "/api/v1/oauth";
var AuthClient = class {
  constructor(opts) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.http = opts.fetch ?? defaultFetch;
    this.baseHeaders = {
      "X-Playserv-Client": opts.clientToken,
      "X-PlayServ-Public-Key": opts.clientToken,
      ...opts.projectId !== void 0 ? { "X-Project-Id": opts.projectId } : {}
    };
  }
  /** `GET /api/v1/oauth/providers` — providers enabled for this project. */
  async getProviders() {
    const data = await this.request("GET", `${LEGACY}/providers`);
    const list = Array.isArray(data) ? data : isObject(data) && Array.isArray(data["providers"]) ? data["providers"] : isObject(data) && Array.isArray(data["items"]) ? data["items"] : [];
    return list;
  }
  /** `POST /auth/players/{provider}:start` — begin the OAuth browser flow. */
  async startOAuth(provider, opts = {}) {
    const data = await this.request("POST", `${RUNTIME}/${provider}:start`, {
      redirectOrigin: opts.redirectOrigin,
      nonce: opts.nonce,
      codeChallenge: opts.codeChallenge,
      codeChallengeMethod: opts.codeChallengeMethod
    });
    return {
      authorizeUrl: str(field(data, "authorizeUrl", "AuthorizeUrl")) ?? "",
      state: str(field(data, "state", "State")) ?? ""
    };
  }
  /** `POST /auth/players/{provider}:callback` — finish the browser flow. */
  async completeOAuth(provider, code, state, codeVerifier) {
    const data = await this.request("POST", `${RUNTIME}/${provider}:callback`, {
      code,
      state,
      codeVerifier
    });
    return toSessionToken(data);
  }
  /**
   * Native token verification. Hand a provider-issued token to the platform.
   * `body` is the provider-specific payload (`GoogleNativeVerifyRequest`,
   * `AppleNativeVerifyRequest`, `FacebookLimitedLoginRequest`); facebook uses
   * the `:verify-limited` path. Epic native verify is not offered on the
   * runtime plane — use the browser `startOAuth`/`completeOAuth` flow for Epic.
   */
  async verifyNative(provider, body) {
    const path = provider === "facebook" ? `${RUNTIME}/facebook:verify-limited` : `${RUNTIME}/${provider}:verify`;
    const data = await this.request("POST", path, body);
    return toSessionToken(data);
  }
  /**
   * `POST /auth/players/refresh` — exchange a refresh token for a fresh access
   * JWT (and a rotated refresh token). The response carries no `playerId`; the
   * caller retains it from the originating sign-in.
   */
  async refresh(refreshToken) {
    const data = await this.request("POST", `${RUNTIME}/refresh`, { refreshToken });
    const token = str(field(data, "accessToken", "AccessToken", "token", "Token"));
    const nextRefresh = str(field(data, "refreshToken", "RefreshToken"));
    if (token === void 0 || nextRefresh === void 0) {
      throw new AuthError({
        Code: 0,
        Message: "refresh response missing accessToken/refreshToken",
        ...isObject(data) ? { Details: data } : {}
      });
    }
    return {
      token,
      refreshToken: nextRefresh,
      expiresAt: str(field(data, "expiresAt", "ExpiresAt")) ?? ""
    };
  }
  /** `POST /auth/players/sign-out` — revoke a refresh token server-side. */
  async signOut(refreshToken) {
    await this.request("POST", `${RUNTIME}/sign-out`, { refreshToken });
  }
  async request(method, path, body) {
    const headers = { ...this.baseHeaders };
    if (method === "POST") headers["Content-Type"] = "application/json";
    const res = await this.http({ method, url: `${this.baseUrl}${path}`, headers, body });
    if (res.status < 200 || res.status >= 300) {
      const message = str(field(res.data, "error", "Error", "message", "Message")) ?? `HTTP ${String(res.status)}`;
      throw new AuthError({
        Code: res.status,
        Message: `auth ${method} ${path} failed: ${message}`,
        ...isObject(res.data) ? { Details: res.data } : {}
      });
    }
    return res.data;
  }
};
var defaultFetch = async (req) => {
  const f = globalThis.fetch;
  if (!f) throw new Error("No fetch implementation available \u2014 pass authHttpFetch in config");
  const res = await f(req.url, {
    method: req.method,
    headers: req.headers,
    ...req.body !== void 0 ? { body: JSON.stringify(req.body) } : {}
  });
  let data;
  const text = await res.text();
  try {
    data = text.length > 0 ? JSON.parse(text) : void 0;
  } catch {
    data = text;
  }
  return { status: res.status, data };
};
function isObject(v) {
  return typeof v === "object" && v !== null;
}
function field(data, ...keys) {
  if (!isObject(data)) return void 0;
  for (const k of keys) if (data[k] !== void 0) return data[k];
  return void 0;
}
function str(v) {
  return typeof v === "string" ? v : void 0;
}
function toSessionToken(data) {
  const playerId = str(field(data, "playerId", "PlayerId"));
  const token = str(field(data, "accessToken", "AccessToken", "sessionToken", "SessionToken", "token", "Token"));
  if (playerId === void 0 || token === void 0) {
    throw new AuthError({
      Code: 0,
      Message: "auth response missing playerId/accessToken",
      ...isObject(data) ? { Details: data } : {}
    });
  }
  const expiresAt = str(field(data, "expiresAt", "ExpiresAt")) ?? "";
  const isNew = field(data, "isNew", "IsNew") === true;
  const refreshToken = str(field(data, "refreshToken", "RefreshToken"));
  return {
    playerId,
    token,
    expiresAt,
    isNew,
    ...refreshToken !== void 0 ? { refreshToken } : {}
  };
}

// src/core/PlayServ.ts
var PUBLISH_UNSUPPORTED = "Client-side event publishing is not supported by the platform \u2014 events are published server-side (an @server function / game-server, or a remote() RPC that publishes). Clients receive events with wait() and change state with remote() or model().save().";
var _PlayServClient = class _PlayServClient {
  constructor(clientOpts = {}) {
    this.clientOpts = clientOpts;
    this.config = null;
    this.connection = null;
    this.eventBus = null;
    this.rpcClient = null;
    /** Per-client field-access cache backing auto Data Retrieval (PSV-1348). */
    this.fieldAccess = new FieldAccessRegistry();
    /** Player-auth REST surface (PSV-1696); built in configure() iff httpEndpoint set. */
    this.authClient = null;
    /**
     * The player session obtained from `auth.*` and passed to `ready()`
     * (PSV-1696 phase 2). Drives the token-refresh lifecycle (phase 3). Null ⇒
     * legacy caller-less handshake.
     */
    this.sessionToken = null;
    /** Proactive-refresh timer (PSV-1696 phase 3); null when not scheduled. */
    this.refreshTimer = null;
    /** Single-flight guard for `refreshSession` (M1). */
    this.refreshInFlight = null;
    /** Consecutive proactive-refresh failures — drives retry backoff (C1). */
    this.refreshFailCount = 0;
    /**
     * True between `_close()` and the next `ready()`. Guards the refresh loop so a
     * refresh already in flight when `_close()` runs cannot re-arm a zombie timer
     * or re-persist after the client is torn down (pass-4 N1).
     */
    this.closed = false;
    this.readyPromise = null;
    /**
     * Set of group names the client is currently subscribed to (PSV-1271 M4.5).
     * Used to re-subscribe after reconnect — backend `GroupMembershipManager`
     * is wiped on disconnect (`ClearClientSubscriptions`), so the client must
     * resend `SubscribeGroupRequest` for every group it cares about.
     */
    this.subscribedGroups = /* @__PURE__ */ new Set();
    /** Memoized {@link raw} result — stateless beyond `this`, built once. */
    this.rawConnection = null;
    this.session = new Session();
  }
  configure(cfg) {
    this.config = resolveConfig(cfg);
    this.authClient = this.config.httpEndpoint !== void 0 ? new AuthClient({
      baseUrl: this.config.httpEndpoint,
      clientToken: this.config.gameAccessToken,
      ...this.config.projectId !== void 0 ? { projectId: this.config.projectId } : {},
      ...this.clientOpts.authHttpFetch !== void 0 ? { fetch: this.clientOpts.authHttpFetch } : {}
    }) : null;
  }
  /**
   * Player-auth REST surface (PSV-1696): `getProviders` / `startOAuth` /
   * `completeOAuth` / `verifyNative`. Usable before `ready()`. Requires
   * `config.httpEndpoint`. Wiring the resulting `SessionToken` into the WS
   * handshake + refresh lifecycle is a later phase.
   */
  get auth() {
    if (this.authClient === null) {
      throw new InvalidArgumentError(
        "PlayServ.auth requires config.httpEndpoint \u2014 set it in configure()"
      );
    }
    return this.authClient;
  }
  /**
   * The player session passed to the most recent `ready()` / produced by the
   * last refresh (PSV-1696), or null for a caller-less / not-yet-connected
   * client. Read-only snapshot.
   */
  get playerSession() {
    return this.sessionToken;
  }
  /**
   * Exchange the stored refresh token for a fresh access JWT (PSV-1696 phase 3).
   * Updates `playerSession`, re-arms the proactive-refresh timer, persists the
   * rotated refresh token, and points the connection at the new JWT so the next
   * reconnect re-handshakes with a non-expired token. Call it on a `401`/auth
   * failure to recover without a full re-login. No-op-throws if there is no
   * refresh token or no configured `auth` surface.
   */
  async refreshSession() {
    if (this.refreshInFlight !== null) return this.refreshInFlight;
    this.refreshInFlight = this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }
  async doRefresh() {
    if (this.authClient === null) {
      throw new IllegalStateError("refreshSession requires config.httpEndpoint");
    }
    const current = this.sessionToken;
    if (current?.refreshToken === void 0) {
      throw new IllegalStateError("No refresh token available \u2014 sign in first");
    }
    const rotated = await this.authClient.refresh(current.refreshToken);
    const next = {
      playerId: current.playerId,
      token: rotated.token,
      expiresAt: rotated.expiresAt,
      isNew: false,
      refreshToken: rotated.refreshToken
    };
    this.sessionToken = next;
    this.connection?.setPlayerToken(next.token);
    this.persistSession(next);
    this.refreshFailCount = 0;
    this.scheduleRefresh();
    return next;
  }
  /**
   * Restore a session across restarts (PSV-1696 phase 3): read the persisted
   * refresh token from the storage adapter and exchange it for a fresh session.
   * Returns the restored `SessionToken` (pass it straight to `ready()`), or
   * null when nothing is stored / the refresh is rejected.
   */
  async resumeSession() {
    if (this.authClient === null || this.config === null) return null;
    const raw = this.config.storage.getItem(this.sessionStorageKey());
    if (raw === null) return null;
    let stored;
    try {
      stored = JSON.parse(raw);
    } catch {
      return null;
    }
    if (typeof stored.refreshToken !== "string" || typeof stored.playerId !== "string") {
      return null;
    }
    try {
      const rotated = await this.authClient.refresh(stored.refreshToken);
      const next = {
        playerId: stored.playerId,
        token: rotated.token,
        expiresAt: rotated.expiresAt,
        isNew: false,
        refreshToken: rotated.refreshToken
      };
      this.sessionToken = next;
      this.persistSession(next);
      return next;
    } catch {
      return null;
    }
  }
  /**
   * Sign out (PSV-1696, M2): revoke the refresh token server-side, drop the
   * in-memory session, cancel the refresh timer, and purge the persisted token
   * from storage so no long-lived credential is left behind. Safe to call when
   * not signed in (no-op). Does not close the WS connection — call `_close`/
   * disconnect separately if desired. Server revocation is best-effort: a
   * network failure still clears local state.
   */
  async signOut() {
    this.clearRefreshTimer();
    this.refreshFailCount = 0;
    const refreshToken = this.sessionToken?.refreshToken;
    this.sessionToken = null;
    if (this.config !== null) {
      try {
        this.config.storage.removeItem(this.sessionStorageKey());
      } catch {
      }
    }
    if (refreshToken !== void 0 && this.authClient !== null) {
      try {
        await this.authClient.signOut(refreshToken);
      } catch (err) {
        this.config?.logger.warn("server-side sign-out failed (local state cleared)", err);
      }
    }
  }
  /**
   * Connect and handshake. Pass the `SessionToken` from `auth.completeOAuth` /
   * `auth.verifyNative` (PSV-1696) — or a raw player JWT string — to bind the
   * WS session to a `Player` runtime caller; omit it for a legacy caller-less
   * connection. The token is relayed in the handshake as
   * `Authorization: "Bearer …"` (see `Connection.doHandshake`).
   */
  async ready(token = null) {
    if (this.config === null) {
      throw new InvalidArgumentError("Call PlayServ.configure() before ready()");
    }
    if (this.session.state === "banned") {
      throw new SessionBannedError();
    }
    if (this.session.state === "active" && this.connection !== null) return;
    if (this.readyPromise !== null) return this.readyPromise;
    this.closed = false;
    this.sessionToken = typeof token === "object" && token !== null ? token : null;
    const jwt = typeof token === "string" ? token : token?.token ?? null;
    this.readyPromise = this.doReady(jwt).finally(() => {
      this.readyPromise = null;
    });
    return this.readyPromise;
  }
  async login(externalToken) {
    if (this.connection === null || !this.connection.isOnline) throw new NotReadyError();
    const { token, refreshToken } = await this.connection.login(externalToken);
    const out = { token };
    if (refreshToken !== void 0) out.refreshToken = refreshToken;
    return out;
  }
  send(_topicOrEvent, _data) {
    throw new NotSupportedError(PUBLISH_UNSUPPORTED);
  }
  /** Subscribe to an incoming user event by PascalCase name. */
  wait(eventName, callback) {
    if (this.eventBus === null) throw new NotReadyError();
    return this.eventBus.subscribe(eventName, callback);
  }
  /**
   * Join a group and listen for one of its events (PSV-1271 P7). Convenience
   * over `subscribeToGroup(groupName)` + `wait(eventType, cb)`:
   *   - joins the group (`SubscribeGroupRequest`, in the background) so the
   *     platform gates its events to this connection;
   *   - registers an `EventType`-keyed listener for delivery.
   *
   * The platform strips the group label from delivered events, so `eventType`
   * must be the exact identifier the server emits (typically the payload type's
   * fully-qualified name) and the listener fires for that `eventType` from ANY
   * joined group — it cannot distinguish the source group. `handle.stop()`
   * removes the listener; group membership persists until `unsubscribeFromGroup`.
   */
  waitInGroup(groupName, eventType, callback) {
    if (!groupName) throw new InvalidArgumentError("waitInGroup: groupName is required");
    if (!eventType) throw new InvalidArgumentError("waitInGroup: eventType is required");
    if (this.eventBus === null) throw new NotReadyError();
    void this.subscribeToGroup(groupName).catch((err) => {
      this.config?.logger.warn(`waitInGroup: joining group "${groupName}" failed`, err);
    });
    return this.eventBus.subscribe(eventType, callback);
  }
  /**
   * Build a Proxy for RPC calls to a server module.
   * `moduleName` — server file name without extension, camelCase.
   * `server/domain.js` → `PlayServ.remote('domain')`.
   *
   * `opts.dedupe` coalesces concurrent calls with identical args on the
   * listed methods (CODE_REVIEW.md §3.6). Only safe for idempotent
   * methods — never pass a method with side effects.
   */
  remote(moduleName, opts) {
    if (this.rpcClient === null) throw new NotReadyError();
    return this.rpcClient.remote(moduleName, opts);
  }
  /**
   * Build a query against a `@data` model. `modelName` is the `name` field
   * of the schema (e.g. `'Player'`). Chain `.key() / .where() / .with() /
   * .fields() / .get() / .delete()`.
   */
  model(modelName) {
    const conn = this.requireOnline();
    if (this.config === null) throw new InvalidArgumentError("Missing config");
    return new QueryBuilder(conn, {
      modelName,
      whereConditions: [],
      withClauses: [],
      fields: [],
      logger: this.config.logger.child("data"),
      fieldAccess: this.fieldAccess
    });
  }
  /**
   * NOT supported (PSV-1271 P7a) — clients cannot publish group events on the
   * platform; publishing is server-side (an `@server` function / game-server, or
   * a `remote()` RPC that publishes). Throws `NotSupportedError`. To *receive*
   * a group's events, join it and listen: `subscribeToGroup` + `wait`, or
   * `waitInGroup`.
   */
  publishForGroup(_groupName, _eventType, _payload = {}) {
    throw new NotSupportedError(PUBLISH_UNSUPPORTED);
  }
  /**
   * NOT supported (PSV-1271 P7a) — the platform has no per-user event channel and
   * no client publish path. Throws `NotSupportedError`. Deliver per-player state
   * via a data subscription (`model().key(playerId).listen()`) or a server-side
   * publish.
   */
  publishForUser(_userId, _eventType, _payload = {}) {
    throw new NotSupportedError(PUBLISH_UNSUPPORTED);
  }
  /**
   * Subscribe the current session to a group (PSV-1271 M4.5). After this
   * resolves the client is eligible to receive `GroupEventMessage`
   * deliveries for `groupName` (matched against existing
   * `EventSubscribeRequest`s). Backend handler:
   * `Module.Events/Commands/SubscribeGroupCommandHandler.cs`.
   *
   * The subscription is **per-session**: backend wipes group membership on
   * disconnect (`ClearClientSubscriptions`); the SDK re-subscribes
   * automatically on reconnect.
   */
  async subscribeToGroup(groupName) {
    if (!groupName) {
      throw new InvalidArgumentError("subscribeToGroup: groupName is required");
    }
    if (this.subscribedGroups.has(groupName)) return;
    const conn = this.requireOnline();
    const resp = await conn.request(
      Commands.SubscribeGroupRequest,
      { GroupName: groupName },
      { responseCommand: Commands.SubscribeGroupResponse }
    );
    if (resp.success === false) {
      throw new PlayServError({
        Code: ErrorCodes.INTERNAL_SERVER_ERROR,
        Message: `SubscribeGroupRequest rejected for "${groupName}"`
      });
    }
    this.subscribedGroups.add(groupName);
  }
  /**
   * Leave a group. Backend handler:
   * `Module.Events/Commands/UnsubscribeGroupCommandHandler.cs`.
   * Also drops `groupName` from the local re-subscribe set so the next
   * reconnect doesn't bring it back.
   */
  async unsubscribeFromGroup(groupName) {
    if (!groupName) {
      throw new InvalidArgumentError("unsubscribeFromGroup: groupName is required");
    }
    const conn = this.requireOnline();
    this.subscribedGroups.delete(groupName);
    const resp = await conn.request(
      Commands.UnsubscribeGroupRequest,
      { GroupName: groupName },
      { responseCommand: Commands.UnsubscribeGroupResponse }
    );
    if (resp.success === false) {
      throw new PlayServError({
        Code: ErrorCodes.INTERNAL_SERVER_ERROR,
        Message: `UnsubscribeGroupRequest rejected for "${groupName}"`
      });
    }
  }
  /**
   * Internal handle to the Connection — used by upper layers (RpcClient,
   * Model, Group) once they plug in. Not part of public API.
   * @internal
   */
  _connection() {
    return this.connection;
  }
  /**
   * Advanced escape hatch — see {@link RawConnection}. Memoized: the result
   * is stateless beyond `this`, so it's built once and reused.
   */
  raw() {
    this.rawConnection ?? (this.rawConnection = {
      request: async (command, payload = {}, opts = {}) => this.requireOnline().request(command, payload, opts),
      requestWithId: (command, payload = {}, opts = {}) => {
        try {
          return this.requireOnline().requestWithId(command, payload, opts);
        } catch (err) {
          return { requestId: -1, promise: Promise.reject(err) };
        }
      },
      notify: (command, payload = {}) => {
        try {
          this.requireOnline().notify(command, payload);
        } catch (err) {
          throw err instanceof NotReadyError ? err : new NotReadyError();
        }
      },
      onSystemEvent: (eventName, listener) => this.requireConnection().onSystemEvent(eventName, listener),
      onEventMessage: (listener) => this.requireConnection().onEventMessage(listener)
    });
    return this.rawConnection;
  }
  /** @internal — visible for tests. */
  _eventBus() {
    return this.eventBus;
  }
  /**
   * Aggregate debug snapshot across every subsystem (CODE_REVIEW.md §3.4).
   * Safe to log in development for detecting subscription leaks; do not
   * ship behind user-visible UI in production — payload shape is unstable.
   */
  debugSnapshot() {
    return {
      configured: this.config !== null,
      session: this.session.debugSnapshot,
      connection: this.connection ? this.connection.debugSnapshot : null,
      eventBus: this.eventBus ? this.eventBus.debugSnapshot : null
    };
  }
  /** @internal */
  async _close() {
    this.closed = true;
    this.clearRefreshTimer();
    this.refreshFailCount = 0;
    if (this.connection !== null) {
      await this.connection.close();
      this.connection = null;
    }
    this.eventBus = null;
    this.rpcClient = null;
    this.subscribedGroups.clear();
  }
  // ---------- private ----------
  requireOnline() {
    if (this.connection === null || !this.connection.isOnline) throw new NotReadyError();
    return this.connection;
  }
  /**
   * Looser than {@link requireOnline}: only requires that `ready()` has
   * succeeded at least once, not that the transport is currently open —
   * matches `Connection.onSystemEvent`/`onEventMessage`'s own contract,
   * since registering a listener isn't a live send.
   */
  requireConnection() {
    if (this.connection === null) throw new NotReadyError();
    return this.connection;
  }
  sessionStorageKey() {
    return `playserv:session:${this.config?.gameId ?? "default"}`;
  }
  persistSession(token) {
    if (this.config === null || token.refreshToken === void 0) return;
    try {
      this.config.storage.setItem(
        this.sessionStorageKey(),
        JSON.stringify({ playerId: token.playerId, refreshToken: token.refreshToken })
      );
    } catch {
    }
  }
  /**
   * (Re)arm the proactive-refresh timer for the current session. Fires
   * REFRESH_SKEW_MS before `expiresAt`; an already-expired or unparseable
   * `expiresAt` refreshes almost immediately (m1) rather than silently never.
   * No-op when there is no refresh token.
   */
  scheduleRefresh() {
    this.clearRefreshTimer();
    const token = this.sessionToken;
    if (token?.refreshToken === void 0) return;
    const expiryMs = Date.parse(token.expiresAt);
    const raw = Number.isNaN(expiryMs) ? 1e3 : Math.max(1e3, expiryMs - Date.now() - _PlayServClient.REFRESH_SKEW_MS);
    const delay = Math.min(raw, 2147483647);
    this.armRefreshTimer(delay);
  }
  /**
   * Arm the refresh timer for `delay` ms. On fire it refreshes; on failure it
   * re-arms with exponential backoff (C1) so a transient network/401 blip never
   * permanently stalls the refresh loop (which would let the JWT expire and kill
   * the session). A success resets the backoff and re-arms proactively (via
   * `doRefresh` → `scheduleRefresh`).
   */
  armRefreshTimer(delay) {
    if (this.closed) return;
    this.clearRefreshTimer();
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.refreshSession().then(
        () => {
        },
        (err) => {
          this.refreshFailCount += 1;
          const backoff = Math.min(
            _PlayServClient.REFRESH_RETRY_MAX_MS,
            _PlayServClient.REFRESH_RETRY_BASE_MS * 2 ** (this.refreshFailCount - 1)
          );
          this.config?.logger.warn(
            `proactive token refresh failed (attempt ${String(this.refreshFailCount)}); retrying in ${String(backoff)}ms`,
            err
          );
          this.armRefreshTimer(backoff);
        }
      );
    }, delay);
    this.refreshTimer.unref?.();
  }
  clearRefreshTimer() {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
  async doReady(token) {
    const cfg = this.config;
    if (cfg === null) throw new InvalidArgumentError("Missing config");
    if (this.connection !== null) {
      try {
        await this.connection.close();
      } catch {
      }
      this.connection = null;
      this.eventBus = null;
      this.rpcClient = null;
    }
    const transport = this.buildTransport(cfg);
    const connOpts = {
      transport,
      session: this.session,
      gameAccessToken: cfg.gameAccessToken,
      gameId: cfg.gameId,
      // When authenticated, the authoritative player id IS the identity — use it
      // as the connection/session userId so client-side `session.userId` matches
      // `playerSession.playerId` (and the handshake UserId matches what the server
      // rebinds to). Falls back to the configured userId for caller-less sessions.
      userId: this.sessionToken?.playerId ?? cfg.userId,
      gameVersion: cfg.gameVersion,
      sdkVersion: cfg.sdkVersion,
      allowMultipleConnections: cfg.allowMultipleConnections,
      logger: cfg.logger.child("conn"),
      defaultTimeoutMs: cfg.defaultTimeoutMs
    };
    this.connection = this.clientOpts.connectionFactory ? this.clientOpts.connectionFactory(connOpts) : new Connection(connOpts);
    const eventBusLogger = cfg.logger.child("events");
    this.eventBus = this.clientOpts.eventBusFactory ? this.clientOpts.eventBusFactory(this.connection, eventBusLogger) : new EventBus(this.connection, eventBusLogger);
    const rpcLogger = cfg.logger.child("rpc");
    this.rpcClient = this.clientOpts.rpcClientFactory ? this.clientOpts.rpcClientFactory(this.connection, rpcLogger) : new RpcClient(this.connection, rpcLogger);
    const groupRehydrateLogger = cfg.logger.child("groups");
    this.connection.onRehydrate(async () => {
      if (this.subscribedGroups.size === 0) return;
      const conn = this.connection;
      if (conn === null) return;
      const groups = [...this.subscribedGroups];
      const results = await Promise.allSettled(
        groups.map(
          (g) => conn.request(
            Commands.SubscribeGroupRequest,
            { GroupName: g },
            { responseCommand: Commands.SubscribeGroupResponse }
          )
        )
      );
      results.forEach((r, i) => {
        if (r.status === "rejected") {
          groupRehydrateLogger.warn(
            `re-subscribe to group "${groups[i]}" failed`,
            r.reason
          );
        }
      });
    });
    this.connection.onErrorResponse((_command, _requestId, wireError) => {
      if (this.sessionToken?.refreshToken === void 0) return;
      if (!(classifyProtocolError(wireError) instanceof AuthError)) return;
      void this.refreshSession().catch((err) => {
        cfg.logger.warn("re-auth after auth-error response failed", err);
      });
    });
    await this.connection.bringOnline(token);
    if (this.sessionToken !== null) {
      this.persistSession(this.sessionToken);
      this.scheduleRefresh();
    }
  }
  buildTransport(cfg) {
    if (this.clientOpts.transportFactory) return this.clientOpts.transportFactory(cfg);
    return new WebSocketTransport({
      endpoint: cfg.endpoint,
      keepAliveIntervalMs: cfg.keepAliveIntervalMs,
      reconnect: cfg.reconnect,
      logger: cfg.logger.child("ws")
    });
  }
};
/** Refresh this many ms before the access token's `expiresAt`. */
_PlayServClient.REFRESH_SKEW_MS = 6e4;
/** Base / cap for exponential backoff when a proactive refresh fails (C1). */
_PlayServClient.REFRESH_RETRY_BASE_MS = 5e3;
_PlayServClient.REFRESH_RETRY_MAX_MS = 5 * 6e4;
var PlayServClient = _PlayServClient;
var PlayServ = new PlayServClient();

// src/events/defineEvent.ts
function defineEvent(name, factory) {
  return (...args) => ({ ...factory(...args), _type: name });
}
function isEventEnvelope(value) {
  return typeof value === "object" && value !== null && typeof value._type === "string";
}
/*! Bundled license information:

fast-json-patch/module/helpers.mjs:
  (*!
   * https://github.com/Starcounter-Jack/JSON-Patch
   * (c) 2017-2022 Joachim Wester
   * MIT licensed
   *)

fast-json-patch/module/duplex.mjs:
  (*!
   * https://github.com/Starcounter-Jack/JSON-Patch
   * (c) 2017-2021 Joachim Wester
   * MIT license
   *)
*/

export { AuthClient, AuthError, COMMAND_TO_MODULE, CommandDispatcher, Commands, ConnectionError, ContractMismatchError, DEFAULT_BACKOFF, DataConflictError, DuplicateKeyError, ErrorCodes, EventBus, EventRouter, EventSubscriptionLimitExceededError, FATAL_SESSION_CODES, GameVersionMismatchError, GroupLogicError, IllegalStateError, InvalidArgumentError, Logger, NotFoundError, NotReadyError, NotSupportedError, PlayServ, PlayServClient, PlayServError, ProtocolError, QueryBuilder, RpcClient, RpcExecutionError, SDK_VERSION, SYSTEM_EVENT_NAMES, Session, SessionBannedError, SubscriptionCache, TimeoutError, ValidationError, WebSocketTransport, classifyHandshakeErrorCode, classifyProtocolError, createFieldTracker, defaultStorage, defineEvent, errorClass, errorSubsystem, isCommandResponse, isErrorResponse, isEventEnvelope, isEventMessage, isSystemEvent, memoryStorage, normalizeInboundCommand, normalizeSystemEvent, optionalField, parseInnerPayload, prefixBusCommand, readRequestId, requireField, serializeQuery };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map